import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MultiSelect from "@/components/MultiSelect";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import { toast } from "sonner";
import { ArrowLeft, Upload as UploadIcon, Trophy, Dumbbell, Calendar, Plus, Trash2, Globe, AlertTriangle, X, Lock, Eraser } from "lucide-react";
import { SPORTS, DEFAULT_SPORT, circuitsForSport, agesForSport, GRADES } from "@/lib/sports";
import { gradYearOptionsFromGrades } from "@/lib/gradYears";
import { US_STATES } from "@/lib/usStates";
import { getSavedFilters } from "@/lib/filterCookies";
import { groupIntoPrograms, baseProgramName } from "@/lib/programs";
import { geocodeZip } from "@/lib/geocodeZip";
import { heroForSport } from "@/lib/sportHeros";
import { formatDayShort } from "@/lib/formatDate";
import { FormErrorOverlay } from "@/components/FormErrorOverlay";
import { timezoneForZip, tzAbbrev, tzLongName } from "@/lib/timezone";
import ContactsField, { normContactValue } from "@/components/ContactsField";
import CircuitSelect from "@/components/CircuitSelect";
import PhoneInput from "@/components/PhoneInput";
import CropUpload from "@/components/PhotoUpload";
import PhotoGalleryEditor from "@/components/PhotoGalleryEditor";
import { splitName } from "@/components/NameFields";
import { EVENT_TYPE_OPTIONS } from "@/lib/eventTypes";

// Time options in 15-minute steps, ordered starting at 5:00 AM and wrapping
// through the day back to 4:45 AM. Used by the optional event time pickers.
const TIME_OPTIONS = (() => {
    const out = [];
    for (let i = 0; i < 96; i++) {
        const mins = (300 + i * 15) % 1440;
        const h24 = Math.floor(mins / 60);
        const m = mins % 60;
        const ampm = h24 < 12 ? "AM" : "PM";
        let h12 = h24 % 12; if (h12 === 0) h12 = 12;
        out.push(`${h12}:${String(m).padStart(2, "0")} ${ampm}`);
    }
    return out;
})();

// Minutes past midnight for a "1:00 PM" label (or null).
function labelMinutes(label) {
    const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(label || "").trim());
    if (!m) return null;
    let h = (+m[1]) % 12;
    if (/PM/i.test(m[3])) h += 12;
    return h * 60 + (+m[2]);
}

// Inclusive list of ISO dates between two dates (UTC-safe). Empty for single-day or >31-day ranges.
function eventDaysList(startISO, endISO) {
    const ps = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startISO || "");
    const pe = /^(\d{4})-(\d{2})-(\d{2})$/.exec(endISO || "");
    if (!ps || !pe || endISO <= startISO) return [];
    let cur = Date.UTC(+ps[1], +ps[2] - 1, +ps[3]);
    const end = Date.UTC(+pe[1], +pe[2] - 1, +pe[3]);
    const out = [];
    while (cur <= end && out.length <= 32) {
        const dt = new Date(cur);
        out.push(`${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`);
        cur += 86400000;
    }
    return out.length > 31 ? [] : out;
}

// Snap an AI-detected time ("1:05 PM" or "13:05") to the nearest 15-min option label.
function normalizeTimeToOption(raw) {
    if (!raw) return "";
    const s = String(raw).trim();
    let h, m;
    const m12 = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(s);
    if (m12) { h = (+m12[1]) % 12; if (/PM/i.test(m12[3])) h += 12; m = +m12[2]; }
    else { const m24 = /^(\d{1,2}):(\d{2})$/.exec(s); if (!m24) return ""; h = +m24[1]; m = +m24[2]; }
    if (h > 23 || m > 59) return "";
    let total = (Math.round((h * 60 + m) / 15) * 15) % 1440;
    const hh = Math.floor(total / 60), mn = total % 60;
    const ampm = hh < 12 ? "AM" : "PM";
    let h12 = hh % 12; if (h12 === 0) h12 = 12;
    const label = `${h12}:${String(mn).padStart(2, "0")} ${ampm}`;
    return TIME_OPTIONS.includes(label) ? label : "";
}

// Downscale an oversized image (>5MB) client-side so large phone photos still upload.
const loadImg = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
async function ensureUnder5MB(file) {
    const MAX = 5 * 1024 * 1024;
    if (!file || file.size <= MAX) return file;
    const url = URL.createObjectURL(file);
    try {
        const img = await loadImg(url);
        const type = file.type === "image/png" ? "image/png" : "image/jpeg";
        let scale = Math.min(1, Math.sqrt(MAX / file.size));
        let w = Math.max(1, Math.round(img.width * scale));
        let h = Math.max(1, Math.round(img.height * scale));
        let quality = 0.85;
        let last = null;
        for (let i = 0; i < 7; i++) {
            const canvas = document.createElement("canvas");
            canvas.width = w; canvas.height = h;
            canvas.getContext("2d").drawImage(img, 0, 0, w, h);
            // eslint-disable-next-line no-await-in-loop
            const blob = await new Promise((r) => canvas.toBlob(r, type, quality));
            if (blob) {
                last = blob;
                if (blob.size <= MAX) break;
            }
            w = Math.round(w * 0.85); h = Math.round(h * 0.85);
            if (type === "image/jpeg" && quality > 0.5) quality -= 0.1;
        }
        if (last) return new File([last], file.name.replace(/\.\w+$/, type === "image/png" ? ".png" : ".jpg"), { type });
        return file;
    } catch {
        return file;
    } finally {
        URL.revokeObjectURL(url);
    }
}
const GENDERS = [["M", "Boys"], ["F", "Girls"]];
const MAX_SOCIAL_LINKS = 10;

/** Read the Landing filter cookie and pick sensible single-value defaults for the Add forms. */
function readFilterDefaults() {
    const f = getSavedFilters({});
    const first = (arr) => Array.isArray(arr) && arr.length ? arr[0] : "";
    return {
        state: first(f.states),
        age: first(f.ages),
        grade: first(f.grades),
        gender: first(f.genders) || "M",
        circuit: first(f.circuits),
    };
}

// Returns the list of similar listings when the API blocks a create with a 409
// duplicate-listing response, otherwise null.
function dupMatches(err) {
    const d = err?.response?.data?.detail;
    if (err?.response?.status === 409 && d && typeof d === "object" && d.code === "duplicate_listing") {
        return Array.isArray(d.matches) ? d.matches : [];
    }
    return null;
}

function DuplicateWarningDialog({ warn, saving, onConfirm, onCancel, accent = "#ff4500" }) {
    if (!warn) return null;
    const label = { team: "team", trainer_listing: "trainer", event: "event" }[warn.entity] || "listing";
    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70" data-testid="duplicate-warning-dialog">
            <div className="w-full max-w-md bg-[#141414] border border-[#2a2a2a] p-6">
                <div className="flex items-center gap-2 text-[#f59e0b]">
                    <AlertTriangle className="h-5 w-5" />
                    <h3 className="font-display text-xl tracking-wide">Possible duplicate {label}</h3>
                </div>
                <p className="text-sm text-[#cfcfcf] mt-3">
                    We found {warn.matches.length} existing {label} listing{warn.matches.length === 1 ? "" : "s"} that look{warn.matches.length === 1 ? "s" : ""} similar to what you're adding:
                </p>
                <ul className="mt-3 space-y-2 max-h-48 overflow-auto">
                    {warn.matches.map((m) => (
                        <li key={m.id} className="border border-[#2a2a2a] bg-[#0f0f0f] px-3 py-2">
                            <a href={m.link} target="_blank" rel="noreferrer" className="text-sm text-white hover:underline" data-testid={`dup-match-${m.id}`}>{m.name}</a>
                            {m.detail ? <div className="text-xs text-[#a0a0a0] mt-0.5">{m.detail}</div> : null}
                        </li>
                    ))}
                </ul>
                <p className="text-xs text-[#a0a0a0] mt-3">If this is genuinely different, you can create it anyway — it will be sent to our team for a quick review.</p>
                <div className="flex gap-2 mt-5">
                    <Button type="button" onClick={onCancel} disabled={saving} data-testid="dup-cancel-btn" className="flex-1 h-11 rounded-none bg-[#0f0f0f] border border-[#2a2a2a] hover:border-white uppercase tracking-[0.15em] text-xs">Cancel</Button>
                    <Button type="button" onClick={onConfirm} disabled={saving} data-testid="dup-create-anyway-btn" className="flex-1 h-11 rounded-none uppercase tracking-[0.15em] text-xs font-semibold" style={{ backgroundColor: accent }}>{saving ? "Creating…" : "Create anyway"}</Button>
                </div>
            </div>
        </div>
    );
}


export default function Add() {
    const { sport: sportParam } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, loading } = useAuth();

    const sport = SPORTS.includes(sportParam) ? sportParam : DEFAULT_SPORT;

    // ?type=team|trainer|event optional — carries over from previous filter
    const searchParams = new URLSearchParams(location.search);
    const initialType = ["team", "trainer", "event"].includes(searchParams.get("type")) ? searchParams.get("type") : "event";
    const [type, setType] = useState(initialType);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
        }
    }, [user, loading, navigate, location]);

    if (loading || !user) return <div className="text-center text-[#a0a0a0] py-20">Loading…</div>;

    const heroUrl = heroForSport(sport);

    return (
        <div className="min-h-screen relative" data-testid="add-page">
            {/* Sport-specific hero background */}
            <div className="absolute inset-x-0 top-0 h-72 -z-10">
                <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url("${heroUrl}")`, backgroundSize: "cover", backgroundPosition: "center" }} />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#0a0a0a]" />
            </div>

            <div className="relative max-w-3xl mx-auto px-6 md:px-8 py-10">
                <button
                    onClick={() => navigate(-1)}
                    data-testid="add-back"
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[#ff4500] mb-4"
                >
                    <ArrowLeft className="h-3 w-3" /> Back
                </button>
                <div className="text-[#ff4500] text-xs uppercase tracking-[0.3em] mb-1">Add · {sport}</div>
                <h1 className="font-display text-5xl tracking-wide" data-testid="add-heading">Contribute to the portal</h1>
                <p className="text-sm text-[#cfcfcf] mt-2">
                    Add a team, trainer, or event for <strong>{sport}</strong>. You can change the sport by returning to
                    the previous page or by picking a different sport there.
                </p>

                {/* Type picker */}
                <EmailVerifyBanner />
                <div className="mt-6 grid grid-cols-3 gap-2" data-testid="add-type-picker">
                    <TypeBtn active={type === "team"} onClick={() => setType("team")} icon={Trophy} label="Team" testid="add-type-team" />
                    <TypeBtn active={type === "trainer"} onClick={() => setType("trainer")} icon={Dumbbell} label="Trainer" testid="add-type-trainer" accent="#2196f3" />
                    <TypeBtn active={type === "event"} onClick={() => setType("event")} icon={Calendar} label="Event" testid="add-type-event" accent="#22c55e" />
                </div>

                <div className="mt-8">
                    {type === "team" && <AddTeamForm sport={sport} />}
                    {type === "trainer" && <AddTrainerForm sport={sport} />}
                    {type === "event" && <AddEventForm key={`ev-${searchParams.get("claim_type") || ""}-${searchParams.get("claim_id") || ""}-${searchParams.get("duplicate") || ""}`} sport={sport} claim={{ type: searchParams.get("claim_type") || "", id: searchParams.get("claim_id") || "" }} prefill={{ team_id: searchParams.get("team_id") || "", host_name: searchParams.get("host") || "", duplicate: searchParams.get("duplicate") || "" }} />}
                </div>
            </div>
        </div>
    );
}

function TypeBtn({ active, onClick, icon: Icon, label, testid, accent = "#ff4500" }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            style={active ? { backgroundColor: accent, borderColor: accent } : undefined}
            className={`px-4 py-4 border transition-all inline-flex flex-col items-center gap-1 ${active ? "text-white" : "bg-[#141414] border-[#2a2a2a] text-[#cfcfcf] hover:border-[#ff4500]"}`}
        >
            <Icon className="h-5 w-5" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold">{label}</span>
        </button>
    );
}

/* -------------------- Three-image uploader (avatar / logo / main) -------------------- */
function PhotoImages({ form, setForm, prefix, dragPaste = false, hideAvatar = false, logoLabel = "Logo (square)", logoHint = "Cards, list rows, map pins, thumbnails." }) {
    return (
        <div data-testid={`${prefix}-images`}>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Images</Label>
            <div className={`mt-2 grid grid-cols-1 ${hideAvatar ? "md:grid-cols-1" : "md:grid-cols-2"} gap-3`}>
                {!hideAvatar && (
                    <CropUpload
                        value={form.avatar}
                        onChange={(url) => setForm((f) => ({ ...f, avatar: url }))}
                        circle
                        label="Avatar (circular)"
                        hint="Round pic used in posts & attribution."
                        dragPaste={dragPaste}
                        testid={`${prefix}-avatar-upload`}
                    />
                )}
                <CropUpload
                    value={form.logo}
                    onChange={(url) => setForm((f) => ({ ...f, logo: url }))}
                    aspect={1}
                    square
                    label={logoLabel}
                    hint={logoHint}
                    dragPaste={dragPaste}
                    testid={`${prefix}-logo-upload`}
                />
            </div>
            <LogoBgPicker value={form.logo_bg || "white"} onChange={(v) => setForm((f) => ({ ...f, logo_bg: v }))} logo={form.logo || form.avatar || (form.photos || [])[0]} testid={`${prefix}-logo-bg`} />
            <PhotoGalleryEditor
                photos={form.photos || []}
                onChange={(photos) => setForm((f) => ({ ...f, photos, photo: photos[0] || "" }))}
                max={5}
                dragPaste={dragPaste}
                testid={`${prefix}-gallery`}
            />
            <LogoBgPicker
                value={form.preview_bg || "white"}
                onChange={(v) => setForm((f) => ({ ...f, preview_bg: v }))}
                logo={form.logo || form.avatar || (form.photos || [])[0]}
                testid={`${prefix}-preview-bg`}
                label="Listing preview background"
                hint="Background behind the logo on cards, list rows &amp; map pins."
            />
        </div>
    );
}

// Lets the owner pick the logo background — square swatches for both the detail-page
// badge and the card/list/map preview.
function LogoBgPicker({ value, onChange, logo, testid = "logo-bg",
    label = "Logo badge background",
    hint = "Background & border of the square logo shown on the detail page." }) {
    const shape = "rounded-none";
    const opts = [
        { id: "clear", label: "Clear", box: "bg-transparent border border-dashed border-[#555]" },
        { id: "black", label: "Black", box: "bg-black border-2 border-black" },
        { id: "white", label: "White", box: "bg-white border-2 border-white" },
    ];
    return (
        <div className="mt-4" data-testid={testid}>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{label}</Label>
            <div className="text-[10px] text-[#666] mt-0.5 mb-2">{hint}</div>
            <div className="flex gap-2 flex-wrap">
                {opts.map((o) => {
                    const active = value === o.id;
                    return (
                        <button
                            key={o.id}
                            type="button"
                            data-testid={`${testid}-${o.id}`}
                            onClick={() => onChange(o.id)}
                            className={`flex items-center gap-2 px-3 py-2 border transition-colors ${active ? "border-[#ff4500] bg-[#ff4500]/10" : "border-[#2a2a2a] hover:border-[#555]"}`}
                        >
                            <span className={`h-8 w-8 ${shape} overflow-hidden flex items-center justify-center shrink-0 ${o.box}`}>
                                {logo ? <img src={logo} alt="" className={`w-full h-full ${shape} object-contain ${o.id === "clear" ? "" : "p-0.5"}`} /> : null}
                            </span>
                            <span className={`text-xs uppercase tracking-[0.15em] ${active ? "text-white" : "text-[#a0a0a0]"}`}>{o.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* -------------------- Photo uploader (shared) — click, drag-drop, or paste -------------------- */
function PhotoUpload({ value, onChange, testid = "photo-upload" }) {
    const [uploading, setUploading] = useState(false);
    const [drag, setDrag] = useState(false);
    const pasteRef = useRef(null);
    const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");
    const pasteKey = isMac ? "⌘V" : "Ctrl+V";

    const uploadFile = async (file) => {
        if (!file) return;
        if (!file.type?.startsWith("image/")) { toast.error("Please use an image file (JPG/PNG/WebP)"); return; }
        setUploading(true);
        try {
            const finalFile = await ensureUnder5MB(file);  // auto-resize large images instead of rejecting
            const fd = new FormData();
            fd.append("file", finalFile);
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.url);
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onFile = (e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; };

    const onDrop = (e) => {
        e.preventDefault();
        setDrag(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) uploadFile(file);
    };

    // Extract an image from a paste/clipboard event.
    const handleClipboard = (e) => {
        const items = e.clipboardData?.items || [];
        for (const it of items) {
            if (it.type && it.type.startsWith("image/")) {
                const file = it.getAsFile();
                if (file) { uploadFile(file); return true; }
            }
        }
        return false;
    };

    const onBoxPaste = (e) => {
        e.preventDefault();
        if (!handleClipboard(e)) toast.error("No image found in clipboard — copy an image first, then paste");
        if (pasteRef.current) pasteRef.current.textContent = "";
    };

    // Convenience: paste anywhere on the page while this uploader is mounted (event flyer only).
    useEffect(() => {
        const onPaste = (e) => handleClipboard(e);
        document.addEventListener("paste", onPaste);
        return () => document.removeEventListener("paste", onPaste);
        // eslint-disable-next-line
    }, []);

    return (
        <div>
            <div
                data-testid={`${testid}-dropzone`}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={onDrop}
                className={`border bg-[#0f0f0f] px-3 py-2 flex items-center gap-3 flex-wrap transition-colors ${drag ? "border-[#ff4500] bg-[#ff4500]/5" : "border-[#2a2a2a]"}`}
            >
                {value ? (
                    <img src={value} alt="preview" className="h-10 w-14 object-cover border border-[#2a2a2a] flex-none" />
                ) : (
                    <div className="h-10 w-14 border border-dashed border-[#3a3a3a] flex items-center justify-center text-center text-[#666] text-[9px] uppercase tracking-wider flex-none">
                        {drag ? "Drop" : "No photo"}
                    </div>
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 h-9 border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-wider transition-colors flex-none">
                    <UploadIcon className="h-3.5 w-3.5" />
                    {uploading ? "Uploading…" : (value ? "Replace" : "Upload")}
                    <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={testid} />
                </label>
                <span className="hidden sm:inline text-[10px] text-[#a0a0a0] whitespace-nowrap">Drag &amp; drop, or paste <span className="text-[#cfcfcf] font-semibold">{pasteKey}</span></span>
                {/* Right-clickable paste target: an editable region so the browser's context menu shows "Paste". Hidden on mobile (no drag/paste there). */}
                <div className="relative hidden sm:inline-block flex-none">
                    <div
                        ref={pasteRef}
                        contentEditable
                        suppressContentEditableWarning
                        role="textbox"
                        aria-label="Paste image here"
                        tabIndex={0}
                        data-testid={`${testid}-paste-box`}
                        onPaste={onBoxPaste}
                        onInput={(e) => { e.currentTarget.textContent = ""; }}
                        onKeyDown={(e) => { if (!(e.ctrlKey || e.metaKey)) e.preventDefault(); }}
                        className="w-[210px] h-9 px-2 flex items-center border border-dashed border-[#3a3a3a] hover:border-[#ff4500] focus:border-[#ff4500] outline-none cursor-text text-[10px] text-transparent caret-transparent"
                    />
                    <span className="pointer-events-none absolute inset-0 flex items-center px-2 text-[10px] uppercase tracking-wider text-[#a0a0a0]">
                        Right-click ▸ Paste (or {pasteKey})
                    </span>
                </div>
                {value && (
                    <button type="button" onClick={() => onChange("")} className="text-[10px] uppercase tracking-wider text-[#a0a0a0] hover:text-red-500 ml-auto flex-none">Remove</button>
                )}
            </div>
            <div className="text-[10px] text-[#666] mt-1">JPG/PNG/WebP · thumbnail on map &amp; list, full-size on detail page.</div>
        </div>
    );
}

/* -------------------- ADD: TEAM -------------------- */
export function AddTeamForm({ sport, existing, onDone, hideNamePhotos = false }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const editMode = !!existing;
    const rep = editMode ? existing.rosters[0] : null;
    // Multi-team programs are led by a DIRECTOR at the program level (each roster has
    // its own head coach below). Lock the top block to Director in that case.
    const multiRoster = editMode && (existing?.rosters?.length || 0) > 1;
    const defaults = readFilterDefaults();
    const validCircuits = circuitsForSport(sport);
    const validAges = agesForSport(sport);
    const repSplit = splitName(rep?.coach || "");
    // Claim-lock: on a CLAIMED team, the director/head-coach name + the verifying
    // contact can only be edited by the verified owner; everyone else sees them locked.
    const claimed = editMode && !!rep?.owner_verified;
    const isClaimOwner = claimed && !!user && rep?.owner_user_id === user.id;
    const lockQualifying = claimed && !isClaimOwner;
    const claimedContactKey = rep?.claimed_contact
        ? `${(rep.claimed_contact.type || "").toLowerCase()}|${normContactValue(rep.claimed_contact.type, rep.claimed_contact.value)}`
        : null;
    const contactLockKeys = lockQualifying && claimedContactKey ? [claimedContactKey] : [];
    const contactCheckKeys = isClaimOwner && claimedContactKey ? [claimedContactKey] : [];
    const initGenders = editMode ? [...new Set(existing.rosters.map((r) => (r.gender === "F" ? "F" : "M")))] : [];
    const initGrades = editMode ? [...new Set(existing.rosters.map((r) => r.grade).filter(Boolean))] : [];
    const initAges = editMode ? [...new Set(existing.rosters.map((r) => r.age_group).filter(Boolean))] : [];
    const initGradYears = editMode ? [...new Set(existing.rosters.map((r) => r.grad_year).filter(Boolean))] : [];
    const initCircuit = editMode ? (rep?.circuit || (rep?.circuits && rep.circuits[0]) || "") : "";
    const [form, setForm] = useState({
        name: editMode ? existing.base_name : "",
        coach_role: multiRoster ? "Director" : ((editMode && rep?.coach_role) || "Director"),
        coach_first: repSplit.first_name || "", coach_mi: repSplit.middle_initial || "", coach_last: repSplit.last_name || "",
        city: editMode ? (rep?.city || "") : "", state: editMode ? (rep?.state || "") : defaults.state, zip: editMode ? (rep?.zip || "") : "", street_address: editMode ? (rep?.street_address || "") : "",
        genders: initGenders,
        ages: initAges,
        grades: initGrades,
        gradYears: initGradYears,
        circuit: initCircuit,
        website: editMode ? (rep?.website || "") : "",
        description: editMode ? (rep?.description || "") : "", photo: editMode ? (rep?.photo || "") : "", logo: editMode ? (rep?.logo || "") : "", avatar: "",
        photos: editMode ? ((rep?.photos && rep.photos.length) ? rep.photos : (rep?.photo ? [rep.photo] : [])) : [],
        logo_bg: editMode ? (rep?.logo_bg || "white") : "white",
        preview_bg: editMode ? (rep?.preview_bg || "white") : "white",
    });
    // One-time prompt: when a program has become multi-roster (a Director slot is now
    // required), ask the owner to confirm/update the director's name — shown once per program.
    const dirPromptKey = editMode ? `dir-confirm:${(existing?.base_name || rep?.name || "").toLowerCase()}` : null;
    const [showDirPrompt, setShowDirPrompt] = useState(() => {
        try { return !!(multiRoster && dirPromptKey && !localStorage.getItem(dirPromptKey)); } catch { return false; }
    });
    const dismissDirPrompt = () => {
        try { if (dirPromptKey) localStorage.setItem(dirPromptKey, "1"); } catch { /* ignore */ }
        setShowDirPrompt(false);
    };
    // A US ZIP authoritatively determines its state. On edit, correct any stale stored
    // state (and backfill a missing city) from the ZIP so it can't drift out of sync.
    useEffect(() => {
        if (!editMode || (form.zip || "").length !== 5) return;
        geocodeZip(form.zip).then((p) => {
            if (!p) return;
            setForm((f) => ({ ...f, state: p.state, city: (f.city || "").trim() ? f.city : p.city }));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [contacts, setContacts] = useState(() => {
        if (editMode) {
            let list;
            if (Array.isArray(rep?.contacts) && rep.contacts.length) {
                list = rep.contacts.map((c) => ({ type: c.type, value: c.value }));
            } else {
                list = [];
                if (rep?.contact_phone) list.push({ type: "phone", value: rep.contact_phone });
                if (rep?.contact_email) list.push({ type: "email", value: rep.contact_email });
            }
            // Fold any legacy standalone website into the editable contacts so it can be edited/deleted.
            const legacySite = (rep?.website || "").trim();
            if (legacySite && !list.some((c) => c.type === "website" && (c.value || "").trim() === legacySite)) {
                list.push({ type: "website", value: legacySite });
            }
            return list.length ? list : [{ type: "phone", value: "" }];
        }
        return [{ type: "phone", value: "" }];
    });
    // Extra social media URLs — array of strings, one per input. Max 10.
    const [socialLinks, setSocialLinks] = useState(editMode && Array.isArray(rep?.social_media_links) && rep.social_media_links.length ? rep.social_media_links : [""]);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");
    const [dupWarn, setDupWarn] = useState(null);
    // ----- Edit mode: existing rosters shown as editable rows (boys→girls, older→younger) -----
    const rosterRank = (r) => {
        if (r.age_group) return parseInt(r.age_group, 10) || 0;
        if (r.grade) return (parseInt(r.grade, 10) || 0) + 5;
        if (r.grad_year) return 2100 - (parseInt(r.grad_year, 10) || 0);
        return 0;
    };
    const [toDelete, setToDelete] = useState([]);
    const [rosterEdits, setRosterEdits] = useState(() => {
        const acc = {};
        for (const r of (existing?.rosters || [])) {
            const tc = Array.isArray(r.team_coaches) && r.team_coaches.length ? r.team_coaches[0] : null;
            const s = splitName(tc?.name || "");
            const cs = tc?.contacts || [];
            acc[r.id] = {
                first_name: s.first_name || "", middle_initial: s.middle_initial || "", last_name: s.last_name || "",
                email: (cs.find((c) => c.type === "email") || {}).value || "",
                phone: (cs.find((c) => c.type === "phone") || {}).value || "",
                dim: r.age_group ? "age" : r.grade ? "grade" : r.grad_year ? "gradyear" : "age",
                identifier: r.age_group || r.grade || r.grad_year || "",
            };
        }
        return acc;
    });
    const patchRoster = (id, patch) => setRosterEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));
    const rosterLabel = (r) => `${r.gender === "F" ? "Girls" : "Boys"} ${r.age_group || r.grade || r.grad_year || ""}`.trim();
    // Trash button on a roster row: queue delete + uncheck its grade/age above
    // if no other remaining team still uses that identifier.
    const deleteRosterRow = (r) => {
        if (!window.confirm(`Delete ${rosterLabel(r)}? This permanently removes the team and its info when you save.`)) return;
        const nextDelete = [...new Set([...toDelete, r.id])];
        setToDelete(nextDelete);
        const dim = r.age_group ? "age" : r.grade ? "grade" : "gradyear";
        const ident = r.age_group || r.grade || r.grad_year || "";
        const stillUsed = existing.rosters.some((x) => !nextDelete.includes(x.id) && (x.age_group || x.grade || x.grad_year || "") === ident);
        if (ident && !stillUsed) {
            if (dim === "age") setForm((f) => ({ ...f, ages: f.ages.filter((v) => v !== ident) }));
            else if (dim === "grade") setForm((f) => ({ ...f, grades: f.grades.filter((v) => v !== ident) }));
        }
    };
    const sortedRosters = editMode
        ? [...existing.rosters].filter((r) => !toDelete.includes(r.id)).sort((a, b) => {
            const ga = a.gender === "F" ? 1 : 0, gb = b.gender === "F" ? 1 : 0;
            if (ga !== gb) return ga - gb;
            return rosterRank(b) - rosterRank(a);
        })
        : [];
    const identifierOptionsFor = (dim) => (dim === "grade" ? GRADES : validAges);
    // Deleting a team by deselecting its grade/age from the multiselect (edit mode).
    const handleDimChange = (dim, newVals) => {
        const field = dim === "age" ? "ages" : dim === "gradyear" ? "gradYears" : "grades";
        const rosterVal = (r) => (dim === "age" ? r.age_group : dim === "gradyear" ? r.grad_year : r.grade);
        if (!editMode) { setForm((f) => ({ ...f, [field]: newVals })); return; }
        const oldVals = form[field];
        const removed = oldVals.filter((v) => !newVals.includes(v));
        const removedExisting = removed.flatMap((v) => existing.rosters.filter((r) => rosterVal(r) === v));
        if (removedExisting.length) {
            const names = removedExisting.map(rosterLabel).join(", ");
            if (!window.confirm(`Delete ${names}? This permanently removes the team and its info when you save.`)) return;
            setToDelete((prev) => [...new Set([...prev, ...removedExisting.map((r) => r.id)])]);
        }
        const added = newVals.filter((v) => !oldVals.includes(v));
        if (added.length) {
            const readd = added.flatMap((v) => existing.rosters.filter((r) => rosterVal(r) === v)).map((r) => r.id);
            if (readd.length) setToDelete((prev) => prev.filter((id) => !readd.includes(id)));
        }
        setForm((f) => ({ ...f, [field]: newVals }));
    };
    const handleGendersChange = (newVals) => {
        if (!editMode) { setForm((f) => ({ ...f, genders: newVals })); return; }
        const removed = form.genders.filter((g) => !newVals.includes(g));
        const removedExisting = removed.flatMap((g) => existing.rosters.filter((r) => (r.gender === "F" ? "F" : "M") === g));
        if (removedExisting.length) {
            const names = removedExisting.map(rosterLabel).join(", ");
            if (!window.confirm(`Delete ${names}? This permanently removes the team(s) and info when you save.`)) return;
            setToDelete((prev) => [...new Set([...prev, ...removedExisting.map((r) => r.id)])]);
        }
        const added = newVals.filter((g) => !form.genders.includes(g));
        if (added.length) {
            const readd = added.flatMap((g) => existing.rosters.filter((r) => (r.gender === "F" ? "F" : "M") === g)).map((r) => r.id);
            if (readd.length) setToDelete((prev) => prev.filter((id) => !readd.includes(id)));
        }
        setForm((f) => ({ ...f, genders: newVals }));
    };
    // Per-team head coach entries for NEW teams only — mirrors the compressed block.
    // `overrides` maps a team key (e.g. "Girls 4th") -> { first_name, ..., contacts:[{type,value}] }.
    const [selectedTeamKey, setSelectedTeamKey] = useState("");
    const [overrides, setOverrides] = useState({});
    const tcGenderOpts = form.genders.length ? form.genders : ["M", "F"];
    const tcGradeOpts = [...form.grades, ...form.ages, ...form.gradYears];
    const existingKeySet = new Set((existing?.rosters || []).map((r) => `${r.gender === "F" ? "Girls" : "Boys"} ${r.age_group || r.grade || r.grad_year || ""}`.trim()));
    const allTeamKeys = tcGenderOpts.flatMap((g) => tcGradeOpts.map((ident) => `${g === "F" ? "Girls" : "Boys"} ${ident}`));
    // Only offer head-coach entry for NEW teams (existing teams are edited in rows above).
    const showTeamCoach = editMode
        ? allTeamKeys.some((k) => !existingKeySet.has(k))
        : (form.ages.length + form.grades.length + form.gradYears.length) > 1;
    const teamOptions = allTeamKeys.filter((k) => !existingKeySet.has(k) && !overrides[k]).map((k) => [k, k]);
    const joinName = (p) => {
        if (!p) return "";
        const mi = (p.middle_initial || "").trim();
        return [(p.first_name || "").trim(), mi ? `${mi.charAt(0).toUpperCase()}.` : "", (p.last_name || "").trim()].filter(Boolean).join(" ");
    };
    const setOverrideField = (key, field, val) => setOverrides((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: val } }));
    // Coach contacts: Phone/Email only, phone default. "Add other" adds the unused type.
    const coachContacts = (key) => (overrides[key]?.contacts) || [{ type: "phone", value: "" }];
    const setCoachContacts = (key, arr) => setOverrideField(key, "contacts", arr);
    const updateCoachContact = (key, i, patch) => setCoachContacts(key, coachContacts(key).map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
    const addOtherContact = (key) => {
        const cur = coachContacts(key);
        const used = new Set(cur.map((c) => c.type));
        const next = ["phone", "email"].find((t) => !used.has(t));
        if (next) setCoachContacts(key, [...cur, { type: next, value: "" }]);
    };
    const removeCoachContact = (key, i) => {
        const cur = coachContacts(key);
        setCoachContacts(key, cur.length <= 1 ? [{ type: "phone", value: "" }] : cur.filter((_, idx) => idx !== i));
    };
    const coachContactsList = (v) => (v?.contacts || []).map((c) => ({ type: c.type, value: (c.value || "").trim() })).filter((c) => c.value);
    // A coach entry is valid only with a Last name AND at least one contact.
    const coachValid = (v) => !!(v && (v.last_name || "").trim() && coachContactsList(v).length);
    // Build the team_coaches payload from valid overrides on submit.
    const buildTeamCoaches = () => Object.entries(overrides)
        .filter(([, v]) => coachValid(v))
        .map(([team, v]) => ({ team, role: "Head Coach", name: joinName(v), contacts: coachContactsList(v) }));
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const addSocial = () => {
        if (socialLinks.length >= MAX_SOCIAL_LINKS) {
            toast.info(`You can add up to ${MAX_SOCIAL_LINKS} links`);
            return;
        }
        setSocialLinks([...socialLinks, ""]);
    };
    const updateSocial = (idx, val) => {
        setSocialLinks(socialLinks.map((v, i) => (i === idx ? val : v)));
    };
    const removeSocial = (idx) => {
        const next = socialLinks.filter((_, i) => i !== idx);
        setSocialLinks(next.length ? next : [""]);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return setFormError("Please enter a team name.");
        if (!form.coach_first.trim() || !form.coach_last.trim()) return setFormError("Please enter the director / head coach's first and last name.");
        if ((form.zip || "").length !== 5) return setFormError("Please enter a valid 5-digit ZIP code.");
        if (!form.city.trim()) return setFormError("Please enter a city (it auto-fills once you enter the ZIP).");
        if (!form.genders.length) return setFormError("Please select at least one gender.");
        if (!form.grades.length && !form.ages.length && !form.gradYears.length) return setFormError("Enter at least one age group, grade, or grad year.");
        if (!form.circuit) return setFormError("Please select a circuit.");
        const cleanedContacts = contacts.map((c) => ({ type: c.type, value: (c.value || "").trim() })).filter((c) => c.value);
        if (!cleanedContacts.length) return setFormError("Please add at least one contact (email, phone or social handle).");
        // Owner editing a claimed team: warn before saving a change that breaks the claim.
        if (editMode && isClaimOwner) {
            const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
            const mi = form.coach_mi.trim();
            const newCoach = [form.coach_first.trim(), mi ? `${mi.charAt(0).toUpperCase()}.` : "", form.coach_last.trim()].filter(Boolean).join(" ");
            const nameChanged = norm(newCoach) !== norm(rep?.coach);
            const verifyGone = claimedContactKey && !cleanedContacts.some((c) => `${(c.type || "").toLowerCase()}|${normContactValue(c.type, c.value)}` === claimedContactKey);
            if (nameChanged || verifyGone) {
                if (!window.confirm("This change no longer matches your account, so your claim will be removed and the team becomes unclaimed. Continue?")) return;
            }
        }
        setSaving(true);
        try {
            const social_media_links = socialLinks.map((u) => u.trim()).filter(Boolean);
            const mi = form.coach_mi.trim();
            const coach = [form.coach_first.trim(), mi ? `${mi.charAt(0).toUpperCase()}.` : "", form.coach_last.trim()].filter(Boolean).join(" ");

            // ---------------- EDIT MODE: update each roster + create/delete ----------------
            if (editMode) {
                const base = form.name.trim();
                const glLabel = (g) => (g === "F" ? "Girls" : "Boys");
                const activeRosters = existing.rosters.filter((r) => !toDelete.includes(r.id));
                const newIdents = [...form.grades.map((v) => ["grade", v]), ...form.ages.map((v) => ["age", v]), ...form.gradYears.map((v) => ["gradyear", v])];
                const existKeys = new Set(existing.rosters.map((r) => `${r.gender === "F" ? "F" : "M"}|${r.age_group || r.grade || r.grad_year || ""}`));
                const toCreate = [];
                for (const g of form.genders) {
                    for (const [dim, ident] of newIdents) {
                        if (!existKeys.has(`${g}|${ident}`)) toCreate.push({ gender: g, dim, ident });
                    }
                }
                const totalAfter = activeRosters.length + toCreate.length;
                const multi = totalAfter > 1;
                const shared = {
                    city: form.city.trim(), state: form.state, zip: form.zip, street_address: form.street_address.trim(),
                    circuit: form.circuit, circuits: [form.circuit],
                    coach, coach_role: form.coach_role,
                    website: "", description: form.description || "", photo: form.photo || "", logo: form.logo || "",
                    photos: form.photos || [],
                    logo_bg: form.logo_bg || "white",
                    preview_bg: form.preview_bg || "white",
                    contacts: cleanedContacts, social_media_links,
                };
                const lockedConflicts = [];
                for (const r of activeRosters) {
                    const ed = rosterEdits[r.id] || {};
                    const dim = ed.dim || (r.age_group ? "age" : r.grade ? "grade" : "gradyear");
                    const id = ed.identifier != null && ed.identifier !== "" ? ed.identifier : (r.age_group || r.grade || r.grad_year || "");
                    const gl = glLabel(r.gender === "F" ? "F" : "M");
                    const hcName = joinName(ed);
                    const hcContacts = [
                        (ed.email || "").trim() ? { type: "email", value: (ed.email || "").trim() } : null,
                        (ed.phone || "").trim() ? { type: "phone", value: (ed.phone || "").trim() } : null,
                    ].filter(Boolean);
                    const team_coaches = (hcName || hcContacts.length)
                        ? [{ team: `${gl} ${id}`.trim(), role: "Head Coach", name: hcName, contacts: hcContacts }]
                        : [];
                    const resp = await api.put(`/teams/${r.id}`, {
                        ...shared,
                        name: multi && id ? `${base} ${gl} ${id}` : base,
                        team_coaches,
                        age_group: dim === "age" ? id : "",
                        grade: dim === "grade" ? id : "",
                        grad_year: dim === "gradyear" ? id : "",
                    });
                    for (const c of (resp.data?.locked_conflicts || [])) {
                        if (!lockedConflicts.find((x) => x.field === c.field)) lockedConflicts.push(c);
                    }
                }
                for (const t of toCreate) {
                    const gl = glLabel(t.gender);
                    const key = `${gl} ${t.ident}`;
                    const ov = overrides[key] || {};
                    const team_coaches = coachValid(ov)
                        ? [{ team: key, role: "Head Coach", name: joinName(ov), contacts: coachContactsList(ov) }]
                        : [];
                    await api.post("/teams", {
                        name: multi && t.ident ? `${base} ${gl} ${t.ident}` : base,
                        sport,
                        age_group: t.dim === "age" ? t.ident : "", grade: t.dim === "grade" ? t.ident : "", grad_year: t.dim === "gradyear" ? t.ident : "",
                        gender: t.gender, circuit: form.circuit, circuits: [form.circuit],
                        city: form.city.trim(), state: form.state, zip: form.zip, street_address: form.street_address.trim(),
                        coach, coach_role: form.coach_role,
                        team_coaches,
                        contacts: cleanedContacts, social_media_links,
                        website: "", description: form.description || "", photo: form.photo || "", logo: form.logo || "",
                        photos: form.photos || [],
                        logo_bg: form.logo_bg || "white",
                        preview_bg: form.preview_bg || "white",
                        program_team_id: rep.id,
                    });
                }
                for (const id of toDelete) await api.delete(`/teams/${id}`);
                toast.success(`${base} updated`);
                if (onDone) onDone(lockedConflicts); else navigate(`/teams/${rep.id}`);
                return;
            }

            // ---------------- CREATE MODE ----------------
            const primary = form.avatar || form.logo || form.photo;
            const imgs = { avatar: form.avatar || primary, logo: form.logo || primary, photo: form.photo || primary };
            const createTeam = async (ack) => {
                const { data } = await api.post("/teams", { ...form, ...imgs, coach, gender: form.genders[0] || "M", sport, grad_years: form.gradYears, social_media_links, contacts: cleanedContacts, team_coaches: buildTeamCoaches(), acknowledge_duplicate: ack });
                toast.success(`Team "${data.name}" added`);
                navigate(`/teams/${data.id}`);
            };
            try {
                await createTeam(false);
            } catch (err) {
                const m = dupMatches(err);
                if (m) { setSaving(false); setDupWarn({ entity: "team", matches: m, confirm: () => createTeam(true) }); return; }
                throw err;
            }
        } catch (err) {
            setFormError(err?.response?.data?.detail || (editMode ? "Failed to save changes. Please try again." : "Failed to add team. Please try again."));
        } finally {
            setSaving(false);
        }
    };

    const confirmDup = async () => {
        if (!dupWarn?.confirm) return;
        setSaving(true);
        try { await dupWarn.confirm(); setDupWarn(null); }
        catch (err) { setSaving(false); setDupWarn(null); setFormError(err?.response?.data?.detail || "Failed to add team. Please try again."); }
    };

    return (
        <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid="add-team-form">
            <FormErrorOverlay message={formError} onClose={() => setFormError("")} testid="team-error-overlay" />
            <DuplicateWarningDialog warn={dupWarn} saving={saving} onConfirm={confirmDup} onCancel={() => setDupWarn(null)} accent="#ff4500" />
            {claimed && isClaimOwner && (
                <div className="border border-amber-500/60 bg-amber-500/10 p-3 text-xs text-amber-300" data-testid="team-claim-warning">
                    <span className="font-semibold uppercase tracking-[0.15em] text-amber-400">Claimed listing</span> — this is verified as yours. Changing the
                    director / head-coach name or removing the contact that matches your account will <strong>unclaim</strong> it (it becomes publicly editable again).
                </div>
            )}
            {claimed && lockQualifying && (
                <div className="border border-[#2a2a2a] bg-[#141414] p-3 text-xs text-[#a0a0a0] flex items-start gap-2" data-testid="team-locked-note">
                    <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#8a8a8a]" />
                    <span>This team is <strong className="text-[#cfcfcf]">claimed</strong>. The director / head-coach name and the verifying contact are locked and can only be changed by the verified owner. You can still edit everything else.</span>
                </div>
            )}
            {!hideNamePhotos && <F label="Team Name" testid="team-name" value={form.name} onChange={set("name")} required />}
            <div data-testid="team-coach-block">
                <div className="flex items-center gap-2.5">
                    {(multiRoster ? ["Director"] : ["Director", "Head Coach"]).map((role, idx) => (
                        <div key={role} className="flex items-center">
                            {idx === 1 && <span className="mx-1 text-[11px] lowercase tracking-normal text-[#a0a0a0]">or</span>}
                            <button
                                type="button"
                                data-testid={`team-coach-role-${role.toLowerCase().replace(/\s+/g, "-")}`}
                                aria-pressed={form.coach_role === role}
                                disabled={lockQualifying}
                                onClick={() => setForm({ ...form, coach_role: role })}
                                className={`px-5 py-2.5 text-xs uppercase tracking-[0.12em] border transition-colors disabled:opacity-60 ${
                                    form.coach_role === role
                                        ? "border-[#ff4500] text-[#ff4500] bg-[#ff4500]/10 font-semibold"
                                        : "border-[#2a2a2a] bg-[#141414] text-[#a0a0a0] hover:border-[#555]"
                                }`}
                            >
                                {role}
                            </button>
                            {idx === 1 && <span className="self-start mt-1 ml-0.5 text-[#ff4500] leading-none">*</span>}
                        </div>
                    ))}
                    {isClaimOwner && <span className="text-[#22c55e] text-[10px] normal-case tracking-normal ml-1">✓ verifies your claim</span>}
                    {lockQualifying && <span className="text-[#8a8a8a] text-[10px] normal-case tracking-normal ml-1 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Locked</span>}
                </div>
                {multiRoster && (
                    <p className="mt-1.5 text-[10px] text-[#8a8a8a] normal-case tracking-normal" data-testid="director-slot-note">
                        This program has multiple teams, so it's led by a <span className="text-[#cfcfcf]">Director</span>. Each team's head coach is listed per roster below.
                    </p>
                )}
                {showDirPrompt && !lockQualifying && (
                    <div className="mt-2 flex items-start gap-2 border border-[#ff4500]/40 bg-[#ff4500]/10 px-3 py-2" data-testid="director-confirm-prompt">
                        <span className="text-[11px] text-[#ffd9c7] leading-relaxed flex-1">
                            This program now has multiple teams, so it needs a <strong className="text-white">Director</strong>. Please confirm or update the director's name below.
                        </span>
                        <button type="button" onClick={dismissDirPrompt} data-testid="director-confirm-dismiss" className="shrink-0 text-[10px] uppercase tracking-[0.15em] text-[#ff4500] hover:text-[#ff6a33] font-semibold">
                            Got it
                        </button>
                    </div>
                )}
                <div className="mt-3 flex items-end gap-2">
                    <F label="First" testid="team-coach-first" value={form.coach_first} onChange={set("coach_first")} disabled={lockQualifying} containerClass="flex-1 min-w-0" required />
                    <F label="M.I." testid="team-coach-mi" value={form.coach_mi} onChange={(e) => setForm({ ...form, coach_mi: e.target.value.slice(0, 1) })} disabled={lockQualifying} compact w="w-16" placeholder="—" />
                    <F label="Last" testid="team-coach-last" value={form.coach_last} onChange={set("coach_last")} disabled={lockQualifying} containerClass="flex-1 min-w-0" required />
                </div>
            </div>
            <div className="flex flex-wrap items-end gap-3" data-testid="team-tags-row">
                <div className="w-auto">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Genders <span className="text-[#ff4500] ml-0.5">*</span></Label>
                    <div className="mt-2"><MultiSelect fit testid="team-genders" label="Genders" placeholder="Add gender" value={form.genders} onChange={handleGendersChange} options={GENDERS} /></div>
                </div>
                <div className="w-auto">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Grades <span className="text-[#ff4500] ml-0.5">*</span></Label>
                    <div className="mt-2"><MultiSelect fit testid="team-grades" label="Grades" placeholder="Add grades" value={form.grades} onChange={(v) => handleDimChange("grade", v)} options={GRADES.map((g) => [g, g])} /></div>
                </div>
                <div className="w-auto">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Age Groups <span className="text-[#ff4500] ml-0.5">*</span></Label>
                    <div className="mt-2"><MultiSelect fit testid="team-ages" label="Ages" placeholder="Add ages" value={form.ages} onChange={(v) => handleDimChange("age", v)} options={validAges.map((a) => [a, a])} /></div>
                </div>
                <div className="w-auto">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Grad Years <span className="text-[#ff4500] ml-0.5">*</span></Label>
                    <div className="mt-2"><MultiSelect fit testid="team-grad-years" label="Grad Years" placeholder="Add grad years" value={form.gradYears} onChange={(v) => handleDimChange("gradyear", v)} options={gradYearOptionsFromGrades().map((y) => [y, y])} /></div>
                </div>
                <div className="w-auto">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Circuit <span className="text-[#ff4500] ml-0.5">*</span></Label>
                    <div className="mt-2"><CircuitSelect sport={sport} multi={false} value={form.circuit} onChange={(v) => setForm({ ...form, circuit: v })} testid="team-circuit" widthClass="w-44" placeholder="Add circuit" /></div>
                </div>
            </div>
            <div className="text-[10px] text-[#666] -mt-1" data-testid="team-age-grade-hint"><span className="text-[#ff4500]">*</span> Enter at least one age group, grade, or grad year.</div>
            <F label="Street Address" testid="team-street-address" value={form.street_address} onChange={set("street_address")} placeholder="e.g. 100 Main St" />
            <div className="flex flex-wrap items-end gap-2">
                <F label="City" testid="team-city" value={form.city} onChange={set("city")} required disabled={form.zip?.length !== 5} containerClass="flex-1 min-w-[8rem]" placeholder={form.zip?.length === 5 ? "" : "Fill ZIP first"} />
                <F label="State" testid="team-state" value={form.state} disabled required compact w="w-16" placeholder="—" />
                <F
                    label="ZIP"
                    testid="team-zip"
                    value={form.zip}
                    onChange={(e) => {
                        const z = (e.target.value || "").replace(/\D/g, "").slice(0, 5);
                        setForm((f) => ({ ...f, zip: z }));
                        if (z.length === 5) geocodeZip(z).then((p) => p && setForm((f) => ({ ...f, city: p.city, state: p.state })));
                    }}
                    inputMode="numeric"
                    pattern="[0-9]{5}"
                    required
                    compact
                    w="w-24"
                    placeholder="fill first"
                />
            </div>
            <ContactsField value={contacts} onChange={setContacts} testidPrefix="team-contact" lockedKeys={contactLockKeys} checkKeys={contactCheckKeys} />
            {editMode && sortedRosters.length > 0 && (
                <div className="border border-[#2a2a2a] bg-[#0a0a0a]" data-testid="edit-teams-list">
                    <div className="divide-y divide-[#2a2a2a]">
                        {sortedRosters.map((r) => {
                            const ed = rosterEdits[r.id] || {};
                            const dim = ed.dim || (r.age_group ? "age" : r.grade ? "grade" : "gradyear");
                            const dimLabel = dim === "grade" ? "Grade" : dim === "gradyear" ? "Grad Year" : "Age";
                            const autoInputCls = "mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border border-[#2a2a2a] px-2 text-white outline-none focus:border-[#ff4500] placeholder:text-[#4a4a4a]";
                            return (
                                <div key={r.id} className="p-3 flex flex-wrap items-end gap-2" data-testid={`edit-roster-row-${r.id}`}>
                                    <div className="w-auto flex flex-col items-start">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Gender</Label>
                                        <div data-testid={`edit-gender-${r.id}`} className="mt-1 h-9 inline-flex items-center px-3 text-sm text-white bg-[#0a0a0a] border border-[#2a2a2a]">
                                            {r.gender === "F" ? "Girls" : "Boys"}
                                        </div>
                                    </div>
                                    <div className="w-auto flex flex-col items-start">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">{dimLabel}</Label>
                                        <select
                                            data-testid={`edit-identifier-${r.id}`}
                                            value={ed.identifier != null ? ed.identifier : (r.age_group || r.grade || r.grad_year || "")}
                                            onChange={(e) => patchRoster(r.id, { identifier: e.target.value })}
                                            className="mt-1 h-9 w-auto rounded-none bg-[#0f0f0f] border border-[#2a2a2a] text-sm pl-2 pr-7 text-white"
                                        >
                                            {dim === "gradyear"
                                                ? [ed.identifier || r.grad_year].filter(Boolean).map((y) => <option key={y} value={y}>{y}</option>)
                                                : identifierOptionsFor(dim).map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    </div>
                                    <div className="w-auto flex flex-col items-start">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">First</Label>
                                        <input data-testid={`edit-coach-${r.id}-first`} size={Math.max(4, (ed.first_name || "").length + 1)} value={ed.first_name || ""} onChange={(e) => patchRoster(r.id, { first_name: e.target.value })} className={autoInputCls} />
                                    </div>
                                    <div className="w-auto flex flex-col items-start">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">M.I.</Label>
                                        <input data-testid={`edit-coach-${r.id}-mi`} size={2} maxLength={1} value={ed.middle_initial || ""} onChange={(e) => patchRoster(r.id, { middle_initial: e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 1).toUpperCase() })} className={`${autoInputCls} text-center`} />
                                    </div>
                                    <div className="w-auto flex flex-col items-start">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Last</Label>
                                        <input data-testid={`edit-coach-${r.id}-last`} size={Math.max(4, (ed.last_name || "").length + 1)} value={ed.last_name || ""} onChange={(e) => patchRoster(r.id, { last_name: e.target.value })} className={autoInputCls} />
                                    </div>
                                    <div className="w-auto flex flex-col items-start">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Email</Label>
                                        <input data-testid={`edit-email-${r.id}`} type="email" size={Math.max(4, (ed.email || "").length + 1)} value={ed.email || ""} onChange={(e) => patchRoster(r.id, { email: e.target.value })} className={autoInputCls} />
                                    </div>
                                    <div className="w-auto flex flex-col items-start">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Phone</Label>
                                        <PhoneInput data-testid={`edit-phone-${r.id}`} value={ed.phone || ""} onChange={(v) => patchRoster(r.id, { phone: v })} className="mt-1 h-9 w-36 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a]" />
                                    </div>
                                    <button
                                        type="button"
                                        data-testid={`edit-delete-${r.id}`}
                                        onClick={() => deleteRosterRow(r)}
                                        aria-label={`Delete ${rosterLabel(r)}`}
                                        title="Delete team"
                                        className="mt-1 h-9 w-9 shrink-0 border border-red-600 text-red-500 hover:bg-red-600 hover:text-white flex items-center justify-center transition-colors"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {showTeamCoach && (
                <div className="border border-dashed border-[#2a2a2a] p-4 bg-[#0a0a0a]" data-testid="add-head-coach-section">
                    <div className="text-[10px] text-[#a0a0a0] mb-2">
                        Optional — add a Head Coach for a team. Teams without one stay grouped in the summary above.
                    </div>
                    <div className="w-fit">
                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Add Head Coach for team</Label>
                        <Select value={selectedTeamKey} onValueChange={setSelectedTeamKey}>
                            <SelectTrigger data-testid="team-override-selector" className="mt-1 h-9 w-auto gap-3 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a]">
                                <SelectValue placeholder="Select a team…" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none">
                                {teamOptions.map(([v, lbl]) => <SelectItem key={v} value={v}>{lbl}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {selectedTeamKey && (
                        <div className="mt-3 space-y-2" data-testid={`team-override-form-${selectedTeamKey}`}>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-[#ff4500]">Head Coach · {selectedTeamKey}</div>
                            <div className="grid grid-cols-12 gap-3">
                                <div className="col-span-6">
                                    <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">First <span className="normal-case tracking-normal text-[#666]">(optional)</span></Label>
                                    <Input data-testid={`override-coach-${selectedTeamKey}-first`} value={(overrides[selectedTeamKey] || {}).first_name || ""} onChange={(e) => setOverrideField(selectedTeamKey, "first_name", e.target.value)} className="mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a]" />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">M.I.</Label>
                                    <Input data-testid={`override-coach-${selectedTeamKey}-mi`} maxLength={1} placeholder="—" value={(overrides[selectedTeamKey] || {}).middle_initial || ""} onChange={(e) => setOverrideField(selectedTeamKey, "middle_initial", e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 1).toUpperCase())} className="mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a] text-center" />
                                </div>
                                <div className="col-span-4">
                                    <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Last <span className="text-[#ff4500]">*</span></Label>
                                    <Input data-testid={`override-coach-${selectedTeamKey}-last`} value={(overrides[selectedTeamKey] || {}).last_name || ""} onChange={(e) => setOverrideField(selectedTeamKey, "last_name", e.target.value)} className="mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a]" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Contact <span className="text-[#ff4500]">*</span> <span className="normal-case tracking-normal text-[#666]">(at least one)</span></Label>
                                <div className="mt-1 space-y-2">
                                    {coachContacts(selectedTeamKey).map((c, i) => (
                                        <div key={i} className="flex gap-2 items-center" data-testid={`override-contact-row-${selectedTeamKey}-${i}`}>
                                            <select
                                                data-testid={`override-contact-type-${selectedTeamKey}-${i}`}
                                                value={c.type}
                                                onChange={(e) => updateCoachContact(selectedTeamKey, i, { type: e.target.value })}
                                                className="h-9 w-24 shrink-0 rounded-none bg-[#0f0f0f] border border-[#2a2a2a] text-xs px-2 text-white"
                                            >
                                                <option value="phone">Phone</option>
                                                <option value="email">Email</option>
                                            </select>
                                            {c.type === "phone" ? (
                                                <PhoneInput data-testid={`override-contact-value-${selectedTeamKey}-${i}`} value={c.value} onChange={(v) => updateCoachContact(selectedTeamKey, i, { value: v })} className="h-9 w-40 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a]" />
                                            ) : (
                                                <Input data-testid={`override-contact-value-${selectedTeamKey}-${i}`} type="email" placeholder="coach@example.com" value={c.value} onChange={(e) => updateCoachContact(selectedTeamKey, i, { value: e.target.value })} className="h-9 w-60 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a]" />
                                            )}
                                            {coachContacts(selectedTeamKey).length > 1 && (
                                                <button type="button" data-testid={`override-contact-remove-${selectedTeamKey}-${i}`} onClick={() => removeCoachContact(selectedTeamKey, i)} aria-label="Remove contact" className="h-9 w-9 shrink-0 border border-[#2a2a2a] text-[#a0a0a0] hover:border-red-500 hover:text-red-500 flex items-center justify-center transition-colors">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {coachContacts(selectedTeamKey).length < 2 && (
                                    <button type="button" data-testid={`override-add-other-${selectedTeamKey}`} onClick={() => addOtherContact(selectedTeamKey)} className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#ff4500] hover:underline">
                                        <Plus className="h-3 w-3" /> Add other
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    {Object.entries(overrides).filter(([, v]) => coachValid(v)).length > 0 && (
                        <div className="mt-3 space-y-1 text-[11px] text-[#e5e5e5]" data-testid="team-overrides-set">
                            {Object.entries(overrides)
                                .filter(([, v]) => coachValid(v))
                                .map(([k, v]) => (
                                    <div key={k} className="flex items-center justify-between gap-2 border border-[#2a2a2a] px-2 py-1" data-testid={`tc-row-${k}`}>
                                        <div className="min-w-0 truncate">
                                            <span className="text-[#ff4500]">{k}</span> · {joinName(v) || "—"}{coachContactsList(v).map((c, i) => <span key={i}> · {c.value}</span>)}
                                        </div>
                                        <button
                                            type="button"
                                            data-testid={`tc-remove-${k}`}
                                            className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[#ff4500]"
                                            onClick={() => setOverrides((prev) => { const next = { ...prev }; delete next[k]; if (selectedTeamKey === k) setSelectedTeamKey(""); return next; })}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            )}
            <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">About</Label>
                <Textarea data-testid="team-description" value={form.description} onChange={set("description")} rows={4} className="mt-2 rounded-none bg-[#141414] border-[#2a2a2a]" />
            </div>
            {!hideNamePhotos && <PhotoImages form={form} setForm={setForm} prefix="team" dragPaste hideAvatar />}
            <Button type="submit" disabled={saving} data-testid="add-team-submit" className="w-full h-12 rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-[0.2em] text-xs font-semibold">
                {saving ? (editMode ? "Saving…" : "Adding…") : editMode ? "Save Changes" : `Add ${sport} Team`}
            </Button>
        </form>
    );
}

/* -------------------- ADD: TRAINER (fan-created unclaimed listing) -------------------- */
export function AddTrainerForm({ sport, existing, onDone }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const editMode = !!existing;
    // Claim-lock: a CLAIMED listing's qualifying fields (POC name + the contact that
    // verifies the claim) are editable only by the verified owner. Everyone else
    // (collaborators / moderators / admin) sees them locked. The owner gets a ✓ and a
    // warning that changing them will unclaim the listing.
    const claimed = editMode && !!existing.owner_verified;
    const isClaimOwner = claimed && !!user && existing.owner_user_id === user.id;
    const lockQualifying = claimed && !isClaimOwner;
    const claimedContactKey = existing?.claimed_contact
        ? `${(existing.claimed_contact.type || "").toLowerCase()}|${normContactValue(existing.claimed_contact.type, existing.claimed_contact.value)}`
        : null;
    const contactLockKeys = lockQualifying && claimedContactKey ? [claimedContactKey] : [];
    const contactCheckKeys = isClaimOwner && claimedContactKey ? [claimedContactKey] : [];
    const defaults = readFilterDefaults();
    const splitName = (full) => {
        const p = (full || "").trim().split(/\s+/).filter(Boolean);
        if (p.length === 0) return { first: "", mi: "", last: "" };
        if (p.length === 1) return { first: p[0], mi: "", last: "" };
        if (p.length === 2) return { first: p[0], mi: "", last: p[1] };
        return { first: p[0], mi: p[1].replace(/\.$/, ""), last: p.slice(2).join(" ") };
    };
    const initPoc = editMode ? splitName(existing.poc_name || "") : { first: "", mi: "", last: "" };
    const [form, setForm] = useState(editMode ? {
        facility_name: existing.facility_name || existing.name || "",
        poc_first: initPoc.first, poc_mi: initPoc.mi, poc_last: initPoc.last,
        city: existing.city || "", state: existing.state || "", home_zip: existing.home_zip || "", street_address: existing.street_address || "",
        bio: existing.bio || "", website: existing.website || "", photo: existing.photo || existing.detail_photo || "", logo: existing.logo || "", avatar: existing.avatar || "",
        photos: (existing.photos && existing.photos.length) ? existing.photos : ((existing.photo || existing.detail_photo) ? [existing.photo || existing.detail_photo] : []),
        logo_bg: existing.logo_bg || "white",
        preview_bg: existing.preview_bg || "white",
    } : {
        facility_name: "", poc_first: "", poc_mi: "", poc_last: "",
        city: "", state: defaults.state, home_zip: "", street_address: "",
        bio: "", website: "", photo: "", logo: "", avatar: "",
        photos: [],
        logo_bg: "white",
        preview_bg: "white",
    });
    // Keep the stored state in sync with the ZIP on edit (a US ZIP fixes its state).
    useEffect(() => {
        if (!editMode || (form.home_zip || "").length !== 5) return;
        geocodeZip(form.home_zip).then((p) => {
            if (!p) return;
            setForm((f) => ({ ...f, state: p.state, city: (f.city || "").trim() ? f.city : p.city }));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [trainerContacts, setTrainerContacts] = useState(() => {
        if (editMode) {
            if (Array.isArray(existing.contacts) && existing.contacts.length) return existing.contacts.map((c) => ({ type: c.type, value: c.value }));
            const fb = [];
            if (existing.contact_phone) fb.push({ type: "phone", value: existing.contact_phone });
            if (existing.contact_email) fb.push({ type: "email", value: existing.contact_email });
            return fb.length ? fb : [{ type: "phone", value: "" }];
        }
        return [{ type: "phone", value: "" }];
    });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");
    const [dupWarn, setDupWarn] = useState(null);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        const pocName = [form.poc_first.trim(), form.poc_mi.trim() ? form.poc_mi.trim().replace(/\.$/, "") + "." : "", form.poc_last.trim()].filter(Boolean).join(" ").trim();
        const facility = form.facility_name.trim();
        const displayName = facility || pocName;
        if (!displayName || displayName.length < 2) return setFormError("Please enter a business name and/or a point-of-contact name.");
        if ((form.home_zip || "").length !== 5) return setFormError("Please enter a valid 5-digit ZIP code.");
        if (!form.city.trim()) return setFormError("Please enter a city (it auto-fills once you enter the ZIP).");
        if (!form.bio.trim()) return setFormError("Please add a short bio.");
        const cleanedContacts = trainerContacts.map((c) => ({ type: c.type, value: (c.value || "").trim() })).filter((c) => c.value);
        if (!cleanedContacts.length) return setFormError("Please add at least one contact (email, phone or social handle).");
        // Owner editing a claimed listing: warn before saving a change that breaks the claim.
        if (editMode && isClaimOwner) {
            const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
            const nameChanged = norm(pocName) !== norm(existing.poc_name);
            const verifyGone = claimedContactKey && !cleanedContacts.some((c) => `${(c.type || "").toLowerCase()}|${normContactValue(c.type, c.value)}` === claimedContactKey);
            if (nameChanged || verifyGone) {
                if (!window.confirm("This change no longer matches your account, so your claim will be removed and the listing becomes unclaimed. Continue?")) return;
            }
        }
        setSaving(true);
        try {
            const primary = form.avatar || form.logo || form.photo;
            const hero = (form.photos && form.photos[0]) || form.photo || primary;
            const payload = {
                name: displayName,
                facility_name: facility,
                poc_name: pocName,
                sport,
                city: form.city,
                state: form.state,
                home_zip: form.home_zip,
                street_address: form.street_address,
                bio: form.bio,
                website: form.website,
                contacts: cleanedContacts,
                photo: hero || primary,
                detail_photo: hero || primary,
                photos: form.photos || [],
                logo: form.logo || primary,
                avatar: form.avatar || primary,
                logo_bg: form.logo_bg || "white",
                preview_bg: form.preview_bg || "white",
            };
            if (editMode) {
                const { data: upd } = await api.put(`/trainer-listings/${existing.id}`, payload);
                toast.success(`${displayName} updated`);
                if (onDone) onDone(upd?.locked_conflicts || []); else navigate(`/trainer-listings/${existing.id}`);
                return;
            }
            const createTrainer = async (ack) => {
                const { data } = await api.post("/trainer-listings", { ...payload, acknowledge_duplicate: ack });
                toast.success(`Trainer "${data.name}" added`);
                navigate(`/trainer-listings/${data.id}`);
            };
            try {
                await createTrainer(false);
            } catch (err) {
                const m = dupMatches(err);
                if (m) { setSaving(false); setDupWarn({ entity: "trainer_listing", matches: m, confirm: () => createTrainer(true) }); return; }
                throw err;
            }
        } catch (err) {
            setFormError(err?.response?.data?.detail || "Failed to add trainer. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const confirmDup = async () => {
        if (!dupWarn?.confirm) return;
        setSaving(true);
        try { await dupWarn.confirm(); setDupWarn(null); }
        catch (err) { setSaving(false); setDupWarn(null); setFormError(err?.response?.data?.detail || "Failed to add trainer. Please try again."); }
    };

    return (
        <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid="add-trainer-form">
            <FormErrorOverlay message={formError} onClose={() => setFormError("")} testid="trainer-error-overlay" />
            <DuplicateWarningDialog warn={dupWarn} saving={saving} onConfirm={confirmDup} onCancel={() => setDupWarn(null)} accent="#2196f3" />
            {!editMode && (
            <div className="border border-[#2196f3]/40 bg-[#2196f3]/10 p-3 text-xs text-[#cfcfcf]">
                Add a trainer or training business you know. This creates an <strong>unclaimed community listing</strong> that anyone can help
                improve — and the real trainer can later claim to manage it.
            </div>
            )}
            {editMode && existing.owner_verified && isClaimOwner && (
                <div className="border border-amber-500/60 bg-amber-500/10 p-3 text-xs text-amber-300" data-testid="trainer-claim-warning">
                    <span className="font-semibold uppercase tracking-[0.15em] text-amber-400">Claimed listing</span> — this is verified as yours. Changing the
                    point-of-contact name or removing the contact that matches your account will <strong>unclaim</strong> it (it becomes publicly editable again).
                </div>
            )}
            {editMode && existing.owner_verified && lockQualifying && (
                <div className="border border-[#2a2a2a] bg-[#141414] p-3 text-xs text-[#a0a0a0] flex items-start gap-2" data-testid="trainer-locked-note">
                    <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#8a8a8a]" />
                    <span>This listing is <strong className="text-[#cfcfcf]">claimed</strong>. The point-of-contact name and the verifying contact are locked and can only be changed by the verified owner. You can still edit everything else.</span>
                </div>
            )}
            <div>
                <F label={`Business Name`} testid="trainer-facility-name" value={form.facility_name} onChange={set("facility_name")} placeholder="e.g. Duval Elite Training Center" />
                <p className="mt-1 text-[10px] text-[#8a8a8a]">Enter a business name and/or a point of contact below — <strong className="text-[#cfcfcf]">at least one is required</strong>. Whatever you enter shows on the listing.</p>
            </div>
            <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Trainer or Business Point of Contact{isClaimOwner && <span className="text-[#22c55e] ml-2 normal-case tracking-normal">✓ verifies your claim</span>}{lockQualifying && <span className="text-[#8a8a8a] ml-2 normal-case tracking-normal inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Locked</span>}</Label>
                <div className="flex flex-wrap items-end gap-2 mt-2">
                    <F testid="trainer-poc-first" value={form.poc_first} onChange={set("poc_first")} disabled={lockQualifying} containerClass="flex-1 min-w-[7rem]" placeholder="First" />
                    <F testid="trainer-poc-mi" value={form.poc_mi} onChange={set("poc_mi")} disabled={lockQualifying} compact w="w-14" placeholder="M.I." />
                    <F testid="trainer-poc-last" value={form.poc_last} onChange={set("poc_last")} disabled={lockQualifying} containerClass="flex-1 min-w-[7rem]" placeholder="Last" />
                </div>
                <p className="mt-1 text-[10px] text-[#8a8a8a]">Needed to claim this listing later — this name must match your account name. Without a point of contact, the listing can't be claimed.</p>
            </div>
            <F label="Street Address" testid="trainer-street-address" value={form.street_address} onChange={set("street_address")} placeholder="e.g. 100 Riverview Park Dr (or leave blank — Not listed)" />
            <div className="flex flex-wrap items-end gap-2">
                <F label="City" testid="trainer-city" value={form.city} onChange={set("city")} disabled={form.home_zip?.length !== 5} containerClass="flex-1 min-w-[8rem]" placeholder={form.home_zip?.length === 5 ? "" : "Fill ZIP first"} />
                <F label="State" testid="trainer-state" value={form.state} disabled compact w="w-16" placeholder="—" />
                <F
                    label="ZIP Code"
                    testid="trainer-zip"
                    value={form.home_zip}
                    onChange={(e) => {
                        const z = (e.target.value || "").replace(/\D/g, "").slice(0, 5);
                        setForm((f) => ({ ...f, home_zip: z }));
                        if (z.length === 5) geocodeZip(z).then((p) => p && setForm((f) => ({ ...f, city: p.city, state: p.state })));
                    }}
                    inputMode="numeric"
                    pattern="[0-9]{5}"
                    required
                    compact
                    w="w-24"
                    placeholder="fill first"
                />
            </div>
            <ContactsField value={trainerContacts} onChange={setTrainerContacts} testidPrefix="trainer-contact" lockedKeys={contactLockKeys} checkKeys={contactCheckKeys} />
            <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Bio <span className="text-[#ff4500] ml-0.5">*</span></Label>
                <Textarea data-testid="trainer-bio" value={form.bio} onChange={set("bio")} rows={3} className="mt-2 rounded-none bg-[#141414] border-[#2a2a2a]" />
            </div>
            <PhotoImages form={form} setForm={setForm} prefix="trainer" dragPaste hideAvatar logoLabel="Headshot or Logo" logoHint="Shown on cards, list rows, and map pins." />
            <Button type="submit" disabled={saving} data-testid="add-trainer-submit" className="w-full h-12 rounded-none bg-[#2196f3] hover:bg-[#1e88e5] uppercase tracking-[0.2em] text-xs font-semibold">
                {saving ? (editMode ? "Saving…" : "Adding…") : editMode ? "Save Changes" : `Add ${sport} Trainer`}
            </Button>
        </form>
    );
}

/* -------------------- ADD: EVENT -------------------- */
// Organizer name helpers — split a stored full name into parts for editing,
// and join first/M.I./last back into a single string for storage.
const splitHostName = (full) => {
    const toks = (full || "").trim().split(/\s+/).filter(Boolean);
    if (toks.length === 0) return { host_first: "", host_mi: "", host_last: "" };
    if (toks.length === 1) return { host_first: toks[0], host_mi: "", host_last: "" };
    return { host_first: toks[0], host_last: toks[toks.length - 1], host_mi: toks.slice(1, -1).join(" ").replace(/\.$/, "") };
};
const joinHostName = (first, mi, last) =>
    [(first || "").trim(), (mi || "").trim() ? (mi || "").trim().replace(/\.$/, "") + "." : "", (last || "").trim()].filter(Boolean).join(" ").trim();

export function AddEventForm({ sport, existing, prefill, claim, onDone }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const editMode = !!existing;
    const today = new Date().toISOString().slice(0, 10);
    const defaults = readFilterDefaults();
    const notListed = (v) => (v && v !== "Not listed" ? v : "");
    const [claimListing, setClaimListing] = useState(null); // { name, contacts } of the team/trainer being claimed for
    const [form, setForm] = useState(editMode ? {
        name: existing.name || "", type: existing.type || "", event_date: existing.event_date || "", end_date: existing.end_date || "",
        start_time: existing.start_time || "", end_time: existing.end_time || "",
        location_name: notListed(existing.location_name), street_address: notListed(existing.street_address),
        city: existing.city || "", state: existing.state || "", zip: existing.zip || "",
        description: existing.description || "", host_name: existing.host_name || "", ...splitHostName(existing.organizer_name), website: existing.website || "",
        cost: notListed(existing.cost), photo: existing.photo || "", team_id: existing.team_id || "",
    } : {
        name: "", type: "", event_date: "", end_date: "", start_time: "", end_time: "",
        location_name: "", street_address: "",
        city: "", state: defaults.state, zip: "",
        description: "", host_name: (prefill?.host_name) || "", host_first: "", host_mi: "", host_last: "", website: "", cost: "", photo: "", team_id: (prefill?.team_id) || "",
    });
    // Team Directors can optionally attach the event to one of their PROGRAMS
    // so it shows only on that program's page(s), not across all their programs.
    const [myTeams, setMyTeams] = useState([]);
    useEffect(() => {
        const eligible = !!user && (["team_director", "scout"].includes(user.active_profile) || user.is_admin);
        if (!eligible) return;
        api.get("/my-teams").then(({ data }) => setMyTeams(data || [])).catch(() => {});
    }, [user]);
    // Collapse the owner's rosters into program-level options (one per program, not per
    // 16U/15U roster). The stored value is the program's representative team id; the
    // backend expands it to show the event across every roster in that program.
    const myPrograms = groupIntoPrograms(myTeams);
    // Multi-contact list (Feb 2027): [{type, value}, ...]. Prepopulated from flyer analysis.
    const [contacts, setContacts] = useState(editMode && Array.isArray(existing.contacts) && existing.contacts.length ? existing.contacts.map((c) => ({ type: c.type, value: c.value })) : [{ type: "phone", value: "" }]);

    // When creating an event FOR a claimed listing, fetch it to prefill contacts
    // (director's POC for teams) and drive the claim-requirement warning.
    const claimActive = !editMode && claim?.type && claim?.id;
    useEffect(() => {
        if (!claimActive) return;
        (async () => {
            try {
                const path = claim.type === "team" ? `/teams/${claim.id}` : `/trainer-listings/${claim.id}`;
                const { data } = await api.get(path);
                const listingContacts = (data.contacts || []).map((c) => ({ type: c.type, value: c.value }));
                // For teams the claim only needs the PROGRAM base name (e.g. "Beta Elite 1"),
                // not the specific roster ("Beta Elite 1 Boys 16U").
                const cname = claim.type === "team" ? (baseProgramName(data) || data.name || "") : (data.facility_name || data.name || "");
                setClaimListing({ name: cname, contacts: listingContacts });
                if (listingContacts.length) setContacts(listingContacts);
                setForm((f) => ({ ...f, host_name: f.host_name || cname }));
            } catch { /* ignore */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claimActive, claim?.type, claim?.id]);

    // Claim requirements: title contains the listing name AND one POC matches.
    const normV = (t, v) => {
        const s = (v || "").trim().toLowerCase();
        if (!s) return "";
        if (t === "phone") return s.replace(/\D/g, "");
        return s.replace(/^@/, "").replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "");
    };
    const titleHasName = claimListing ? (form.name || "").toLowerCase().includes((claimListing.name || "").toLowerCase()) && !!claimListing.name : false;
    const pocMatches = claimListing ? (() => {
        const lp = new Set((claimListing.contacts || []).map((c) => `${(c.type || "").toLowerCase()}|${normV(c.type, c.value)}`).filter((x) => !x.endsWith("|")));
        return contacts.some((c) => c.value && lp.has(`${(c.type || "").toLowerCase()}|${normV(c.type, c.value)}`));
    })() : false;
    const claimMet = titleHasName && pocMatches;

    // Per-day schedule for multi-day events, keyed by ISO date → {start, end}.
    const [dayTimes, setDayTimes] = useState(editMode ? (existing.day_times || []).reduce((a, d) => { a[d.date] = { start: d.start || "", end: d.end || "" }; return a; }, {}) : {});
    // Recurrence — only exposed for Trainer / Team Director active profiles (or admin), never in edit mode.
    const canRecur = !!user && (["trainer", "team_director"].includes(user.active_profile) || user.is_admin) && !editMode;
    const [recurrence, setRecurrence] = useState("");   // "" | "weekly" | "monthly"
    const [occurrenceCount, setOccurrenceCount] = useState(4);
    // Date mode: "single" | "range" (add end date) | "separate" (add another date → multi-date series)
    const [dateMode, setDateMode] = useState(editMode && existing.end_date ? "range" : "");
    const EXTRA_MAX = 9;  // up to 10 dates total (main + 9 extra)
    const [extraDates, setExtraDates] = useState([]);
    const addExtraDate = () => setExtraDates((r) => (r.length >= EXTRA_MAX ? r : [...r, { date: form.event_date || "", start_time: "", end_time: "", genders: [], age_groups: [], grades: [], grad_years: [] }]));
    const updateExtra = (i, patch) => setExtraDates((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    const removeExtra = (i) => setExtraDates((r) => r.filter((_, j) => j !== i));
    // Multi-value fields (arrays). Seed from the last-used filter cookie so common
    // combinations transfer over from the Landing filter row without extra clicks.
    const cookieAll = getSavedFilters({});
    const eventAges = agesForSport(sport);
    // On edit, only LOAD the dimensions the creator actually provided (the others were
    // auto cross-mapped and must not appear as user selections). Legacy events (no
    // provided_dims) fall back to loading whatever is stored.
    const providedDims = editMode ? (Array.isArray(existing.provided_dims) && existing.provided_dims.length ? existing.provided_dims : null) : null;
    const loadDim = (d) => !providedDims || providedDims.includes(d);
    const [ageGroups, setAgeGroups] = useState(editMode ? (loadDim("age_groups") ? (existing.age_groups || []) : []) : (Array.isArray(cookieAll.ages) ? cookieAll.ages.filter((a) => eventAges.includes(a)) : []));
    const [eventGrades, setEventGrades] = useState(editMode ? (loadDim("grades") ? (existing.grades || []) : []) : (Array.isArray(cookieAll.grades) ? cookieAll.grades.filter((g) => GRADES.includes(g)) : []));
    const [eventGradYears, setEventGradYears] = useState(editMode ? (loadDim("grad_years") ? (existing.grad_years || []) : []) : []);
    const [genders, setGenders] = useState(editMode ? (existing.genders || []) : (Array.isArray(cookieAll.genders) ? cookieAll.genders : []));
    const [eventTz, setEventTz] = useState(editMode ? (existing.timezone || null) : null);  // IANA tz derived from the ZIP
    // Duplicate: prefill everything from an existing event EXCEPT the date/times.
    useEffect(() => {
        const dupId = prefill?.duplicate;
        if (!dupId || editMode) return;
        (async () => {
            try {
                const { data } = await api.get(`/events/${dupId}`);
                setForm((f) => ({
                    ...f,
                    name: data.name || "", type: data.type || "",
                    location_name: notListed(data.location_name), street_address: notListed(data.street_address),
                    city: data.city || "", state: data.state || f.state, zip: data.zip || "",
                    description: data.description || "", host_name: data.host_name || "", ...splitHostName(data.organizer_name),
                    website: data.website || "", cost: notListed(data.cost), photo: data.photo || "",
                    event_date: "", end_date: "", start_time: "", end_time: "",
                }));
                if (Array.isArray(data.contacts) && data.contacts.length) setContacts(data.contacts.map((c) => ({ type: c.type, value: c.value })));
                if (Array.isArray(data.age_groups)) setAgeGroups(data.age_groups.filter((a) => eventAges.includes(a)));
                if (Array.isArray(data.grades)) setEventGrades(data.grades.filter((g) => GRADES.includes(g)));
                if (Array.isArray(data.grad_years)) setEventGradYears(data.grad_years.map(String));
                if (Array.isArray(data.genders)) setGenders(data.genders);
                if (data.timezone) setEventTz(data.timezone);
                toast.success("Event duplicated — set a new date and save");
            } catch { /* ignore */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefill?.duplicate]);

    const validCircuits = circuitsForSport(sport);
    const [circuits, setCircuits] = useState(editMode ? (existing.circuits || []) : (Array.isArray(cookieAll.circuits) ? cookieAll.circuits.filter((c) => validCircuits.includes(c)) : []));
    const [saving, setSaving] = useState(false);
    const [busyAction, setBusyAction] = useState("");
    const [formError, setFormError] = useState("");
    const [dupWarn, setDupWarn] = useState(null);
    // Flyer autofill state
    const [analyzing, setAnalyzing] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    // Auto-run flyer analysis when a photo is uploaded AND user has said "Yes"
    const analyzeFlyer = async (photoUrl) => {
        if (!photoUrl) return;
        setAnalyzing(true);
        try {
            const { data } = await api.post("/uploads/analyze-flyer", { image_url: photoUrl });
            // Prefill form fields with any non-empty values the model returned
            const nextForm = { ...form, photo: photoUrl };
            const strKeys = ["name", "type", "event_date", "end_date", "location_name",
                             "street_address", "city", "state", "zip", "description",
                             "host_name", "website", "cost"];
            let filled = 0;
            for (const k of strKeys) {
                if (data[k] && typeof data[k] === "string") {
                    nextForm[k] = data[k];
                    filled += 1;
                }
            }
            setForm(nextForm);
            // Times — snap AI-detected values to the nearest 15-minute option.
            const stNorm = normalizeTimeToOption(data.start_time);
            const etNorm = normalizeTimeToOption(data.end_time);
            if (stNorm) { nextForm.start_time = stNorm; filled += 1; }
            if (etNorm) { nextForm.end_time = etNorm; filled += 1; }
            if (stNorm || etNorm) setForm({ ...nextForm });
            // Contacts — merge model-detected contacts (dedupe by type+value).
            const detected = Array.isArray(data.contacts) ? data.contacts : [];
            const validTypes = new Set(["email", "phone", "instagram", "x", "facebook", "website"]);
            const rows = [];
            const seen = new Set();
            for (const c of detected) {
                const t = String(c?.type || "").trim().toLowerCase();
                const v = String(c?.value || "").trim();
                if (!t || !v || !validTypes.has(t)) continue;
                const key = `${t}|${v.toLowerCase()}`;
                if (seen.has(key)) continue;
                seen.add(key);
                rows.push({ type: t, value: v });
            }
            // Legacy fallback: contact_email string from the model.
            if (data.contact_email && typeof data.contact_email === "string") {
                const v = data.contact_email.trim();
                const key = `email|${v.toLowerCase()}`;
                if (v && !seen.has(key)) { rows.push({ type: "email", value: v }); seen.add(key); }
            }
            if (rows.length) {
                setContacts(rows);
                filled += 1;
            }
            if (Array.isArray(data.age_groups) && data.age_groups.length) {
                const valid = data.age_groups.filter((a) => eventAges.includes(a));
                if (valid.length) { setAgeGroups(valid); filled += 1; }
            }
            if (Array.isArray(data.grades) && data.grades.length) {
                const valid = data.grades.filter((g) => GRADES.includes(g));
                if (valid.length) { setEventGrades(valid); filled += 1; }
            }
            if (Array.isArray(data.grad_years) && data.grad_years.length) {
                const valid = data.grad_years.map((y) => String(y).trim()).filter((y) => /^20\d{2}$/.test(y));
                if (valid.length) { setEventGradYears(valid); filled += 1; }
            }
            if (Array.isArray(data.genders) && data.genders.length) {
                const valid = data.genders.filter((g) => ["M", "F"].includes(String(g).toUpperCase())).map((g) => String(g).toUpperCase());
                if (valid.length) { setGenders(valid); filled += 1; }
            }
            if (Array.isArray(data.circuits) && data.circuits.length) {
                const valid = data.circuits.filter((c) => validCircuits.includes(c));
                if (valid.length) { setCircuits(valid); filled += 1; }
            }
            // Date mode — contiguous range vs distinct separate dates (per-day audience/time)
            const dm = String(data.date_mode || "").toLowerCase();
            const isISO = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
            if (dm === "range" && isISO(data.end_date)) {
                setDateMode("range");
            } else if (dm === "separate" && Array.isArray(data.dates) && data.dates.filter((d) => isISO(d?.date)).length > 1) {
                const clean = data.dates.filter((d) => isISO(d?.date));
                setDateMode("separate");
                setRecurrence("");
                nextForm.event_date = clean[0].date;
                nextForm.end_date = "";
                setForm({ ...nextForm });
                const extras = clean.slice(1, 1 + EXTRA_MAX).map((d) => ({
                    date: d.date,
                    start_time: normalizeTimeToOption(d.start_time) || "",
                    end_time: normalizeTimeToOption(d.end_time) || "",
                    genders: Array.isArray(d.genders) ? d.genders.filter((g) => ["M", "F"].includes(String(g).toUpperCase())).map((g) => String(g).toUpperCase()) : [],
                    grades: Array.isArray(d.grades) ? d.grades.filter((g) => GRADES.includes(g)) : [],
                    age_groups: Array.isArray(d.age_groups) ? d.age_groups.filter((a) => eventAges.includes(a)) : [],
                    grad_years: Array.isArray(d.grad_years) ? d.grad_years.map((y) => String(y).trim()).filter((y) => /^20\d{2}$/.test(y)) : [],
                }));
                if (extras.length) { setExtraDates(extras); filled += 1; }
            }
            toast.success(filled ? `Autofilled ${filled} field${filled === 1 ? "" : "s"} — review and edit before submitting` : "Analyzed — no fields could be extracted. Please fill manually.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Flyer analysis failed — please fill fields manually");
        } finally {
            setAnalyzing(false);
        }
    };

    const onPhotoChange = (url) => {
        setForm({ ...form, photo: url });
        if (url && !editMode) analyzeFlyer(url);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return setFormError("Please enter an event name.");
        if (!form.type) return setFormError("Please select an event type.");
        if (!form.event_date) return setFormError("Please choose a start date.");
        if ((form.zip || "").length !== 5) return setFormError("Please enter a valid 5-digit ZIP code.");
        if (!form.city.trim()) return setFormError("Please enter a city (it auto-fills once you enter the ZIP).");
        if (!ageGroups.length && !eventGrades.length && !eventGradYears.length) return setFormError("Pick at least one age group, grade, or grad year.");
        if (!genders.length) return setFormError("Pick at least one gender.");
        // End date / additional dates must be a real, different day (they default to the start date).
        if (dateMode === "range") {
            if (!form.end_date) return setFormError("Please choose an end date, or remove it.");
            if (form.end_date === form.event_date) return setFormError("The end date is the same as the start date — pick a later date, or remove the end date.");
            if (form.end_date < form.event_date) return setFormError("The end date must be after the start date.");
        }
        if (dateMode === "separate") {
            if (!extraDates.some((x) => x.date)) return setFormError("Add at least one additional date, or switch back to a single date.");
            const seen = new Set([form.event_date]);
            for (let i = 0; i < extraDates.length; i++) {
                const d = extraDates[i].date;
                if (!d) return setFormError(`Additional date ${i + 2} is empty — pick a date or remove the row.`);
                if (d === form.event_date) return setFormError(`Additional date ${i + 2} is the same as the start date — change it to a different day.`);
                if (seen.has(d)) return setFormError(`Additional date ${i + 2} is a duplicate — pick a different day.`);
                seen.add(d);
                const sm = labelMinutes(extraDates[i].start_time), em = labelMinutes(extraDates[i].end_time);
                if (sm != null && em != null) {
                    if (em < sm) return setFormError(`Additional date ${i + 2}: end time is before the start time.`);
                    if (em === sm) return setFormError(`Additional date ${i + 2}: end time is the same as the start time — change it, or clear it.`);
                }
            }
        }
        // End-after-start guard.
        const dayList = eventDaysList(form.event_date, form.end_date);
        if (dayList.length > 1) {
            for (let i = 0; i < dayList.length; i++) {
                const dt = dayTimes[dayList[i]] || {};
                const sm = labelMinutes(dt.start), em = labelMinutes(dt.end);
                if (sm != null && em != null) {
                    if (em < sm) return setFormError(`Day ${i + 1}: end time is before the start time — please fix it.`);
                    if (em === sm) return setFormError(`Day ${i + 1}: end time is the same as the start time — change it, or clear it to leave the end optional.`);
                }
            }
        } else if (form.start_time && form.end_time) {
            const sm = labelMinutes(form.start_time), em = labelMinutes(form.end_time);
            if (sm != null && em != null) {
                if (em < sm) return setFormError("End time is before the start time — please fix the times.");
                if (em === sm) return setFormError("End time is the same as the start time — change it, or clear it to leave the end optional.");
            }
        }
        // Collapse contacts — drop empty rows.
        const cleanedContacts = contacts
            .map((c) => ({ type: c.type, value: (c.value || "").trim() }))
            .filter((c) => c.value);
        if (!cleanedContacts.length) {
            return setFormError("Please add at least one contact (email, phone or social handle).");
        }
        if (!form.description.trim()) return setFormError("Please add a description for the event.");
        // Claimed-event flow: warn if the title/POC requirements aren't met yet.
        if (claimActive && !claimMet) {
            const ok = window.confirm(
                `This event will be created as UNCLAIMED because it doesn't yet meet the requirements ` +
                `(title must include "${claimListing?.name || "your listing"}" and one contact must match your listing's POC). ` +
                `You can adjust the event later and claim it. Create it anyway?`
            );
            if (!ok) return;
        }
        setSaving(true);
        try {
            const organizerName = joinHostName(form.host_first, form.host_mi, form.host_last);
            if (editMode) {
                const ep = { ...form, sport, organizer_name: organizerName, age_groups: ageGroups, grades: eventGrades, grad_years: eventGradYears, genders, circuits, contacts: cleanedContacts };
                delete ep.host_first; delete ep.host_mi; delete ep.host_last;
                ep.cost = (form.cost || "").trim() || "Not listed";
                ep.location_name = (form.location_name || "").trim() || "Not listed";
                ep.street_address = (form.street_address || "").trim() || "Not listed";
                ep.day_times = dayList.length > 1 ? dayList.map((d) => ({ date: d, start: (dayTimes[d] || {}).start || "", end: (dayTimes[d] || {}).end || "" })) : [];
                await api.put(`/events/${existing.id}`, ep);
                toast.success(`${form.name} updated`);
                if (onDone) onDone(); else navigate(`/events/${existing.id}`);
                return;
            }
            const payload = { ...form, sport, organizer_name: organizerName, age_groups: ageGroups, grades: eventGrades, grad_years: eventGradYears, genders, circuits, contacts: cleanedContacts };
            delete payload.host_first; delete payload.host_mi; delete payload.host_last;
            payload.cost = (form.cost || "").trim() || "Not listed";
            payload.location_name = (form.location_name || "").trim() || "Not listed";
            payload.street_address = (form.street_address || "").trim() || "Not listed";
            if (dateMode === "separate") {
                const eds = extraDates
                    .filter((x) => x.date)
                    .map((x) => ({
                        date: x.date,
                        start_time: x.start_time || undefined,
                        end_time: x.end_time || undefined,
                        genders: x.genders, age_groups: x.age_groups, grades: x.grades, grad_years: x.grad_years,
                    }));
                if (eds.length) payload.extra_dates = eds;
                delete payload.end_date;
            } else {
                if (canRecur && recurrence && Number(occurrenceCount) > 1) {
                    payload.recurrence = recurrence;
                    payload.occurrence_count = Number(occurrenceCount);
                }
                if (!payload.end_date) delete payload.end_date;
            }
            if (!payload.start_time) delete payload.start_time;
            if (!payload.end_time) delete payload.end_time;
            // Multi-day: send the per-day schedule (backend derives start/end from it).
            if (dayList.length > 1) {
                payload.day_times = dayList.map((d) => ({ date: d, start: (dayTimes[d] || {}).start || "", end: (dayTimes[d] || {}).end || "" }));
            }
            if (!payload.website) delete payload.website;
            if (!payload.photo) delete payload.photo;
            if (!payload.team_id) delete payload.team_id;
            if (claimActive) { payload.claim_entity_type = claim.type; payload.claim_entity_id = claim.id; }
            const createEvent = async (ack) => {
                const { data } = await api.post("/events", { ...payload, acknowledge_duplicate: ack });
                toast.success(data.owner_verified ? `Event "${data.name}" created & claimed` : `Event "${data.name}" added`);
                navigate(`/events/${data.id}`);
            };
            try {
                await createEvent(false);
            } catch (err) {
                const m = dupMatches(err);
                if (m) { setSaving(false); setDupWarn({ entity: "event", matches: m, confirm: () => createEvent(true) }); return; }
                throw err;
            }
        } catch (err) {
            setFormError(err?.response?.data?.detail || "Failed to add event. Please try again.");
        } finally { setSaving(false); }
    };

    const confirmDup = async () => {
        if (!dupWarn?.confirm) return;
        setSaving(true);
        try { await dupWarn.confirm(); setDupWarn(null); }
        catch (err) { setSaving(false); setDupWarn(null); setFormError(err?.response?.data?.detail || "Failed to add event. Please try again."); }
    };

    const eventDayList = eventDaysList(form.event_date, form.end_date);

    // --- Cancel / delete (edit mode only) ---
    const isCreator = editMode && !!user && existing.created_by === user.id;
    const canManage = editMode && !!user && (user.is_admin || isCreator || (existing.owner_verified && existing.owner_user_id === user.id));
    const createdWithin7 = editMode && (existing.created_at ? (Date.now() - new Date(existing.created_at).getTime()) <= 7 * 24 * 3600 * 1000 : true);
    const canDelete = editMode && !!user && (user.is_admin || (isCreator && createdWithin7));
    const cancelDeleteDays = existing?.cancelled_at ? Math.max(0, Math.ceil((new Date(existing.cancelled_at).getTime() + 7 * 24 * 3600 * 1000 - Date.now()) / (24 * 3600 * 1000))) : null;
    const finishAction = () => { if (onDone) onDone(); else navigate(`/events/${existing.id}`); };
    const doCancel = async () => {
        let scope = "single";
        if (existing.series_id && existing.recurrence === "custom") {
            const all = window.confirm("This event has multiple dates.\n\nOK = cancel ALL dates\nCancel = cancel only this one date");
            scope = all ? "all" : "single";
        }
        setBusyAction("cancel");
        try { await api.post(`/events/${existing.id}/cancel`, null, { params: { scope } }); toast.success(scope === "all" ? "All dates cancelled" : "Event marked as cancelled"); finishAction(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Could not cancel event"); }
        finally { setBusyAction(""); }
    };
    const doUncancel = async () => {
        setBusyAction("uncancel");
        try { await api.post(`/events/${existing.id}/uncancel`); toast.success("Event restored"); finishAction(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Could not un-cancel event"); }
        finally { setBusyAction(""); }
    };
    const doDelete = async () => {
        let scope = "single";
        if (existing.series_id) {
            if (existing.recurrence === "custom") {
                const all = window.confirm("This event has multiple dates.\n\nOK = delete ALL dates\nCancel = delete only this one date");
                scope = all ? "all" : "single";
            } else {
                if (!window.confirm("Delete from this recurring series?")) return;
                const all = window.confirm("Delete ALL future dates too?\n\nOK = this date AND every future date in the series\nCancel = only this one date");
                scope = all ? "future" : "single";
            }
        } else {
            if (!window.confirm("Delete this event? If it has followers they're notified and it's removed in 7 days — otherwise it's deleted right away.")) return;
        }
        setBusyAction("delete");
        try {
            const { data } = await api.delete(`/events/${existing.id}`, { params: { scope } });
            if (scope === "future" || scope === "all") {
                const parts = [];
                if (data.deleted) parts.push(`${data.deleted} date${data.deleted === 1 ? "" : "s"} deleted`);
                if (data.scheduled) parts.push(`${data.scheduled} with followers removing in 7 days`);
                toast.success(parts.join(" · ") || "Series removed");
            } else if (data.scheduled) {
                toast.success(data.message || "Followers notified — it will be removed in 7 days");
            } else {
                toast.success("Event deleted");
            }
            navigate("/");
        }
        catch (err) { toast.error(err?.response?.data?.detail || "Could not delete event"); }
        finally { setBusyAction(""); }
    };

    return (
        <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid="add-event-form">
            <FormErrorOverlay message={formError} onClose={() => setFormError("")} testid="event-error-overlay" />
            <DuplicateWarningDialog warn={dupWarn} saving={saving} onConfirm={confirmDup} onCancel={() => setDupWarn(null)} accent="#22c55e" />
            {claimActive && !claimMet && (
                <div className="border border-red-500 bg-red-500/10 p-3 text-xs text-red-300" data-testid="event-claim-note">
                    <span className="font-semibold uppercase tracking-[0.15em] text-red-400">To claim this event</span> — your title must include your
                    {" "}<strong>{claimListing?.name || "team/trainer/business"}</strong> name, and you must have one POC that matches your listing's POC info.
                    {" "}Once both are met, this warning disappears. {titleHasName ? "✓ title" : "✗ title"} · {pocMatches ? "✓ POC" : "✗ POC"}
                </div>
            )}
            {claimActive && claimMet && (
                <div className="border border-[#22c55e] bg-[#22c55e]/10 p-3 text-xs text-[#22c55e]" data-testid="event-claim-ok">
                    ✓ Requirements met — this event will be created as <strong>claimed</strong> and tied to {claimListing?.name}.
                </div>
            )}
            {/* Flyer autofill block — top of the form */}
            <div className="border border-[#2a2a2a] bg-[#141414] p-3 space-y-2" data-testid="event-flyer-block">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-xs text-[#a0a0a0]">{editMode
                        ? <span><span className="text-[#cfcfcf] font-semibold uppercase tracking-[0.15em]">Event flyer / photo</span> — upload a new image to replace the current one.</span>
                        : <span><span className="text-[#cfcfcf] font-semibold uppercase tracking-[0.15em]">Event flyer (Recommended)</span> — upload one and we'll autofill the form (you can still edit anything).</span>}</div>
                    {!editMode && analyzing && <span className="text-[11px] text-[#22c55e]" data-testid="event-flyer-status">Analyzing flyer with AI — this usually takes 5–15 seconds.</span>}
                    {!editMode && !analyzing && form.photo && <span className="text-[11px] text-[#22c55e]" data-testid="event-flyer-status">Analysis complete — review the fields below.</span>}
                </div>
                <PhotoUpload value={form.photo} onChange={onPhotoChange} testid="event-photo-upload" />
            </div>

            <F label="Event Name" testid="event-name" value={form.name} onChange={set("name")} required />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Sel
                    label="Type"
                    testid="event-type"
                    value={form.type}
                    onChange={(v) => setForm({ ...form, type: v })}
                    placeholder="Select Event Type"
                    options={EVENT_TYPE_OPTIONS.map((t) => [t, t])}
                    required
                />
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
                <F label="Start Date" type="date" compact w="w-[10rem]" testid="event-date" value={form.event_date} onChange={set("event_date")} required />
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Add Date</Label>
                    <div className="mt-2">
                        <Sel
                            testid="event-add-date-mode"
                            value={dateMode}
                            placeholder="Optional"
                            onChange={(v) => {
                                setDateMode(v);
                                // Default the new date to the start date (not today) so the picker
                                // opens on the right month and the user must change it before saving.
                                if (v === "range") setForm((f) => ({ ...f, end_date: f.event_date || "" }));
                                else setForm((f) => ({ ...f, end_date: "" }));
                                if (v === "separate") { setRecurrence(""); if (extraDates.length === 0) addExtraDate(); }
                                else setExtraDates([]);
                            }}
                            options={editMode
                                ? [["range", "Add end date"]]
                                : [["range", "Add end date"], ["separate", "Add another date"]]}
                        />
                    </div>
                </div>
                {dateMode === "range" && (
                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">End Date</Label>
                        <div className="mt-2 flex items-center gap-2">
                            <Input data-testid="event-end-date" type="date" value={form.end_date} onChange={set("end_date")} className="h-11 rounded-none bg-[#141414] border-[#2a2a2a] w-[10rem]" />
                            {form.end_date && (
                                <button
                                    type="button"
                                    data-testid="event-end-date-clear"
                                    title="Clear end date"
                                    aria-label="Clear end date"
                                    onClick={() => setForm({ ...form, end_date: "" })}
                                    className="inline-flex items-center justify-center h-11 w-11 border border-[#2a2a2a] bg-[#141414] text-[#a0a0a0] hover:text-[#ff4500] hover:border-[#ff4500] transition-colors shrink-0"
                                >
                                    <Eraser className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {(() => {
                const tzAbb = tzAbbrev(eventTz, form.event_date);
                const tzLong = tzLongName(eventTz, form.event_date);
                const banner = eventTz
                    ? <div className="flex items-center gap-2 px-3 py-2 text-[12px] bg-[#22c55e]/[0.08] border border-[#22c55e]/40 text-[#cfefd8]" data-testid="event-tz-banner"><Globe className="h-4 w-4 shrink-0 text-[#22c55e]" /><span>All times are in <strong className="text-white">{tzLong}</strong> <span className="text-[#ff4500] font-bold">({tzAbb})</span> <span className="text-[#888]">— set from ZIP {form.zip}{form.city ? ` · ${form.city}, ${form.state}` : ""}</span></span></div>
                    : <div className="flex items-center gap-2 px-3 py-2 text-[12px] bg-[#ff4500]/[0.07] border border-[#ff4500]/40 text-[#ffd9c7]" data-testid="event-tz-banner-empty"><Globe className="h-4 w-4 shrink-0 text-[#ff4500]" /><span>Enter the event <strong className="text-white">ZIP</strong> to set the time zone for these times.</span></div>;
                return (
                    <div className="space-y-2">
                        {banner}
                        {eventDayList.length > 1 ? (
                            <DayScheduleFields days={eventDayList} value={dayTimes} onChange={setDayTimes} tzAbbr={tzAbb} />
                        ) : (
                            <div className="flex flex-wrap gap-3">
                                <TimeSelect label="Start Time" testid="event-start-time" value={form.start_time} onChange={(v) => setForm((f) => ({ ...f, start_time: v, end_time: f.end_time || v }))} tzAbbr={tzAbb} />
                                <TimeSelect label="End Time" testid="event-end-time" value={form.end_time} onChange={(v) => setForm((f) => ({ ...f, end_time: v }))} tzAbbr={tzAbb} />
                            </div>
                        )}
                    </div>
                );
            })()}

            {dateMode === "separate" && (() => {
                const tzAbb = tzAbbrev(eventTz, form.event_date);
                return (
                    <div className="border border-[#2a2a2a] bg-[#0f0f0f] p-4 space-y-4" data-testid="event-extra-dates">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Additional Dates</div>
                        <p className="text-[12px] text-[#888] -mt-2">Each date becomes its own listing that all link back to this event. Leave a date's audience blank to inherit the main event's gender / grade / age. Up to 10 dates total.</p>
                        {extraDates.map((row, i) => (
                            <div key={i} className="border border-[#242424] bg-[#141414] p-3 space-y-3" data-testid={`extra-date-row-${i}`}>
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#666]">Date {i + 2}</span>
                                    <button type="button" data-testid={`extra-date-remove-${i}`} onClick={() => removeExtra(i)} className="inline-flex items-center gap-1 text-[11px] text-[#a0a0a0] hover:text-[#ff4500] transition-colors"><Eraser className="h-3.5 w-3.5" /> Remove</button>
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    <div>
                                        <Label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Date</Label>
                                        <Input data-testid={`extra-date-input-${i}`} type="date" value={row.date} onChange={(e) => updateExtra(i, { date: e.target.value })} className="mt-1.5 h-11 rounded-none bg-[#0f0f0f] border-[#2a2a2a] w-[10rem]" />
                                    </div>
                                    <TimeSelect label="Start Time" testid={`extra-start-${i}`} value={row.start_time} onChange={(v) => updateExtra(i, { start_time: v, end_time: row.end_time || v })} tzAbbr={tzAbb} />
                                    <TimeSelect label="End Time" testid={`extra-end-${i}`} value={row.end_time} onChange={(v) => updateExtra(i, { end_time: v })} tzAbbr={tzAbb} />
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    <MultiSelect fit placeholder="Inherit" testid={`extra-genders-${i}`} label="Genders (optional)" value={row.genders} onChange={(v) => updateExtra(i, { genders: v })} options={GENDERS} />
                                    <MultiSelect fit placeholder="Inherit" testid={`extra-grades-${i}`} label="Grades (optional)" value={row.grades} onChange={(v) => updateExtra(i, { grades: v })} options={GRADES.map((g) => [g, g])} />
                                    <MultiSelect fit placeholder="Inherit" testid={`extra-ages-${i}`} label="Ages (optional)" value={row.age_groups} onChange={(v) => updateExtra(i, { age_groups: v })} options={eventAges.map((a) => [a, a])} />
                                    <MultiSelect fit placeholder="Inherit" testid={`extra-grad-years-${i}`} label="Grad Years (optional)" value={row.grad_years} onChange={(v) => updateExtra(i, { grad_years: v })} options={gradYearOptionsFromGrades().map((y) => [y, y])} />
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            data-testid="extra-date-add"
                            onClick={addExtraDate}
                            disabled={extraDates.length >= EXTRA_MAX}
                            className="inline-flex items-center gap-2 h-10 px-4 border border-[#2a2a2a] bg-[#141414] text-[#e5e5e5] hover:border-[#ff4500] hover:text-[#ff4500] transition-colors text-[11px] uppercase tracking-[0.2em] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            + Add another date {extraDates.length >= EXTRA_MAX ? "(max 10)" : ""}
                        </button>
                    </div>
                );
            })()}

            {canRecur && dateMode !== "separate" && (
                <RecurrenceBlock
                    startDate={form.event_date}
                    recurrence={recurrence}
                    setRecurrence={setRecurrence}
                    occurrenceCount={occurrenceCount}
                    setOccurrenceCount={setOccurrenceCount}
                    membershipExpiresAt={user?.membership_expires_at?.[user?.active_profile]}
                    activeProfile={user?.active_profile}
                />
            )}
            <F label="Location Name" testid="event-location-name" value={form.location_name} onChange={set("location_name")} placeholder="e.g. Riverview Park Activities Center (or leave blank — Not listed)" />
            <F label="Street Address" testid="event-street-address" value={form.street_address} onChange={set("street_address")} placeholder="e.g. 100 Riverview Park Dr (or leave blank — Not listed)" />
            <div className="flex flex-wrap items-end gap-2">
                <F label="City" testid="event-city" value={form.city} onChange={set("city")} required disabled={form.zip?.length !== 5} containerClass="flex-1 min-w-[8rem]" placeholder={form.zip?.length === 5 ? "" : "Fill ZIP first"} />
                <F label="State" testid="event-state" value={form.state} disabled required compact w="w-16" placeholder="—" />
                <F
                    label="ZIP"
                    testid="event-zip"
                    value={form.zip}
                    onChange={(e) => {
                        const z = (e.target.value || "").replace(/\D/g, "").slice(0, 5);
                        setForm((f) => ({ ...f, zip: z }));
                        if (z.length === 5) {
                            geocodeZip(z).then((p) => p && setForm((f) => ({ ...f, city: p.city, state: p.state })));
                            timezoneForZip(z).then((tz) => setEventTz(tz));
                        } else {
                            setEventTz(null);
                        }
                    }}
                    inputMode="numeric"
                    pattern="[0-9]{5}"
                    required
                    compact
                    w="w-24"
                    placeholder="fill first"
                />
            </div>
            {/* Multi-select filters — required so search filtering works */}
            <div className="flex flex-wrap gap-3">
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Genders <span className="text-[#ff4500] ml-0.5">*</span></Label>
                    <div className="mt-2">
                        <MultiSelect fit placeholder="Genders" testid="event-genders" label="Genders" value={genders} onChange={setGenders} options={GENDERS} />
                    </div>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Grades <span className="text-[#ff4500] ml-0.5">*</span></Label>
                    <div className="mt-2">
                        <MultiSelect fit placeholder="Grades" testid="event-grades" label="Grades" value={eventGrades} onChange={setEventGrades} options={GRADES.map((g) => [g, g])} />
                    </div>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Age Groups <span className="text-[#ff4500] ml-0.5">*</span></Label>
                    <div className="mt-2">
                        <MultiSelect fit placeholder="Ages" testid="event-age-groups" label="Ages" value={ageGroups} onChange={setAgeGroups} options={eventAges.map((a) => [a, a])} />
                    </div>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Grad Years <span className="text-[#ff4500] ml-0.5">*</span></Label>
                    <div className="mt-2">
                        <MultiSelect fit placeholder="Grad Years" testid="event-grad-years" label="Grad Years" value={eventGradYears} onChange={setEventGradYears} options={gradYearOptionsFromGrades().map((y) => [y, y])} />
                    </div>
                </div>
            </div>
            <div className="text-[10px] text-[#666] -mt-1" data-testid="event-age-grade-hint"><span className="text-[#ff4500]">*</span> Enter at least one age group, grade, or grad year.</div>
            <div className="flex flex-wrap gap-3">
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Circuit</Label>
                    <div className="mt-2">
                        <CircuitSelect sport={sport} multi value={circuits} onChange={setCircuits} accent="#22c55e" testid="event-circuits" leadOptions={["N/A"]} widthClass="w-44" />
                    </div>
                </div>
            </div>
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[12rem]">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Organizer</Label>
                    <F testid="event-host" value={form.host_name} onChange={set("host_name")} containerClass="mt-2" placeholder="Team, Program or Business" />
                    <div className="flex flex-wrap items-end gap-2 mt-2">
                        <F testid="event-host-first" value={form.host_first} onChange={set("host_first")} containerClass="flex-1 min-w-[7rem]" placeholder="First" />
                        <F testid="event-host-mi" value={form.host_mi} onChange={set("host_mi")} compact w="w-14" placeholder="M.I." />
                        <F testid="event-host-last" value={form.host_last} onChange={set("host_last")} containerClass="flex-1 min-w-[7rem]" placeholder="Last" />
                    </div>
                </div>
                <F label="Cost" testid="event-cost" value={form.cost} onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value.replace(/\D/g, "").slice(0, 4) }))} inputMode="numeric" compact w="w-28" placeholder="Not listed" />
            </div>
            <ContactsField value={contacts} onChange={setContacts} testidPrefix="event-contact" />
            {myPrograms.length > 0 && (
                <div data-testid="event-team-attach">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Attach to a specific program (optional)</Label>
                    <select
                        value={form.team_id}
                        onChange={set("team_id")}
                        data-testid="event-team-select"
                        className="mt-2 w-full h-11 rounded-none bg-[#141414] border border-[#2a2a2a] text-white text-sm px-3"
                    >
                        <option value="">All of my programs (shows on every one of my team pages)</option>
                        {myPrograms.map((p) => (
                            <option key={p.key} value={p.representative.id}>{p.base_name}</option>
                        ))}
                    </select>
                    <div className="text-[10px] text-[#666] mt-1">Pick one to show this event only on that program's page.</div>
                </div>
            )}
            <F label="Event Information Source" testid="event-website" value={form.website} onChange={set("website")} placeholder="https://…" />
            <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Description <span className="text-[#ff4500] ml-0.5">*</span></Label>
                <Textarea data-testid="event-description" value={form.description} onChange={set("description")} rows={4} className="mt-2 rounded-none bg-[#141414] border-[#2a2a2a]" required />
            </div>
            <Button type="submit" disabled={saving || analyzing} data-testid="add-event-submit" className="w-full h-12 rounded-none bg-[#22c55e] hover:bg-[#16a34a] uppercase tracking-[0.2em] text-xs font-semibold text-white">
                {saving ? (editMode ? "Saving…" : "Adding…") : editMode ? "Save Changes" : `Add ${sport} Event`}
            </Button>

            {editMode && existing.cancelled_at && canManage && (
                <div className="border border-red-500/50 bg-red-500/10 p-4 space-y-2" data-testid="event-cancelled-state">
                    <div className="flex items-center gap-2 text-red-500 text-[11px] uppercase tracking-[0.18em] font-bold">
                        <AlertTriangle className="h-4 w-4" /> This event is cancelled
                    </div>
                    <p className="text-xs text-[#c98a8a]">Scheduled for automatic deletion in <b>{cancelDeleteDays} day{cancelDeleteDays === 1 ? "" : "s"}</b>. Changed your mind?</p>
                    <button type="button" onClick={doUncancel} disabled={!!busyAction} data-testid="event-uncancel-btn" className="h-9 px-4 border border-[#2a2a2a] bg-[#0f0f0f] text-[#e5e5e5] text-[11px] uppercase tracking-[0.16em] hover:border-[#22c55e] hover:text-[#22c55e] disabled:opacity-50">
                        {busyAction === "uncancel" ? "Restoring…" : "↺ Un-cancel event"}
                    </button>
                </div>
            )}

            {editMode && !existing.cancelled_at && canManage && (
                <div className="border border-red-500/45 bg-red-500/[0.06] p-4" data-testid="event-cancel-zone">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-red-500 font-bold mb-2">Cancel this event</div>
                    <p className="text-xs text-[#a0a0a0] leading-relaxed mb-3">
                        Marks the event as <b className="text-red-500">Cancelled</b>. It will show in red as “(Cancelled?)” on every listing and be
                        <b className="text-red-500"> automatically deleted after 7 days</b>. The event's contacts get an email. You can un-cancel any time before then.
                    </p>
                    <button type="button" onClick={doCancel} disabled={!!busyAction} data-testid="event-cancel-btn" className="w-full h-11 border border-red-500 bg-transparent text-red-500 text-xs uppercase tracking-[0.2em] font-bold hover:bg-red-500/15 disabled:opacity-50 flex items-center justify-center gap-2">
                        <X className="h-4 w-4" /> {busyAction === "cancel" ? "Cancelling…" : "Mark event as cancelled"}
                    </button>
                </div>
            )}

            {canDelete && (
                <div className="border border-[#2a2a2a] p-4" data-testid="event-delete-zone">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#a0a0a0] font-bold mb-2">Delete this event</div>
                    <p className="text-[11px] text-[#8a8a8a] leading-relaxed mb-3">
                        For events created by mistake. Available to the creator for 7 days after creation. Followers will be notified. This is permanent.
                    </p>
                    <button type="button" onClick={doDelete} disabled={!!busyAction} data-testid="event-delete-btn" className="h-9 px-4 border border-[#3a2222] bg-[#0f0f0f] text-red-500 text-[11px] uppercase tracking-[0.16em] hover:bg-red-500/10 disabled:opacity-50 inline-flex items-center gap-2">
                        <Trash2 className="h-3.5 w-3.5" /> {busyAction === "delete" ? "Deleting…" : "Delete event permanently"}
                    </button>
                </div>
            )}
        </form>
    );
}

function F({ label, testid, compact, w, containerClass = "", required, ...props }) {
    const widthCls = compact ? (w || "w-auto") : "w-full";
    return (
        <div className={containerClass}>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{label}{required && <span className="text-[#ff4500] ml-0.5">*</span>}</Label>
            {props.type === "tel" ? (
                <PhoneInput
                    data-testid={testid}
                    value={props.value}
                    onChange={(v) => props.onChange && props.onChange({ target: { value: v } })}
                    required={required}
                    placeholder={props.placeholder}
                    className="mt-2 h-11 rounded-none bg-[#141414] border-[#2a2a2a]"
                />
            ) : (
                <Input data-testid={testid} required={required} {...props} className={`mt-2 h-11 rounded-none bg-[#141414] border-[#2a2a2a] ${widthCls}`} />
            )}
        </div>
    );
}

function TimeSelect({ label, testid, value, onChange, tzAbbr, fullWidth, hideLabel }) {
    return (
        <div>
            {!hideLabel && <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{label}</Label>}
            <Select value={value || ""} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid={testid} className={`${hideLabel ? "" : "mt-2 "}h-11 rounded-none bg-[#141414] border-[#2a2a2a] ${fullWidth ? "w-full" : "w-auto min-w-[7.5rem]"} gap-2 pr-2`}>
                    <SelectValue placeholder="Optional" />
                    {value && tzAbbr ? <span className="ml-1 text-[11px] font-bold text-[#ff4500]">{tzAbbr}</span> : null}
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none max-h-72">
                    <SelectItem value="__none__">— None —</SelectItem>
                    {TIME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
    );
}

function DayScheduleFields({ days, value, onChange, tzAbbr }) {
    const setDay = (date, field, v) => {
        const cur = value[date] || { start: "", end: "" };
        const next = { ...cur, [field]: v };
        // When a start time is picked, default the end to the same time (not a stray default)
        // so the user must change it, or clear it to leave the end optional.
        if (field === "start" && v && !cur.end) next.end = v;
        onChange({ ...value, [date]: next });
    };
    const copyDay1 = () => {
        const d0 = value[days[0]] || { start: "", end: "" };
        const next = {};
        for (const d of days) next[d] = { start: d0.start || "", end: d0.end || "" };
        onChange(next);
    };
    return (
        <div data-testid="event-day-schedule">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-[#22c55e]">Daily Schedule <span className="text-[#666] normal-case tracking-normal">— all times optional</span></span>
                <button type="button" data-testid="event-copy-day1" onClick={copyDay1} className="text-[10px] uppercase tracking-[0.15em] text-[#ff4500] border border-[#2a2a2a] px-2.5 py-1.5 hover:border-[#ff4500] transition-colors">Copy Day 1 to all days</button>
            </div>
            <div className="border border-[#2a2a2a] p-3">
                <div className="grid grid-cols-[4.5rem_1fr_1fr] gap-x-2 gap-y-3 items-center">
                    <div />
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Start</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">End</div>
                    {days.map((d, i) => (
                        <React.Fragment key={d}>
                            <div className="pr-1">
                                <div className="text-sm font-semibold text-white">Day {i + 1}</div>
                                <div className="text-[11px] text-[#a0a0a0]">{formatDayShort(d)}</div>
                            </div>
                            <TimeSelect fullWidth hideLabel testid={`event-day-${i}-start`} value={(value[d] || {}).start || ""} onChange={(v) => setDay(d, "start", v)} tzAbbr={tzAbbr} />
                            <TimeSelect fullWidth hideLabel testid={`event-day-${i}-end`} value={(value[d] || {}).end || ""} onChange={(v) => setDay(d, "end", v)} tzAbbr={tzAbbr} />
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
}


function Sel({ label, testid, value, onChange, options, placeholder, required, compact, minW = "min-w-[4.5rem]", containerClass = "" }) {
    return (
        <div className={containerClass}>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{label}{required && <span className="text-[#ff4500] ml-0.5">*</span>}</Label>
            <Select value={value || ""} onValueChange={onChange}>
                <SelectTrigger data-testid={testid} className={`mt-2 h-11 rounded-none bg-[#141414] border-[#2a2a2a] w-auto ${minW} pr-2`}>
                    <SelectValue placeholder={placeholder || "Select…"} />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none">
                    {options.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
    );
}

// Recurrence block — surfaced only for Trainer / Team Director / admin users. When
// enabled, we display a subscription-aware max occurrence count so the last
// occurrence never lands past the caller's membership expiration.
function RecurrenceBlock({ startDate, recurrence, setRecurrence, occurrenceCount, setOccurrenceCount, membershipExpiresAt, activeProfile }) {
    // Compute the maximum whole occurrences that fit inside the membership window.
    const maxOccurrences = React.useMemo(() => {
        if (!recurrence || !startDate || !membershipExpiresAt) return 52;
        try {
            const start = new Date(startDate + "T00:00:00Z");
            const expiry = new Date(membershipExpiresAt + "T23:59:59Z");
            if (isNaN(start.getTime()) || isNaN(expiry.getTime())) return 52;
            if (expiry < start) return 0;
            if (recurrence === "weekly") {
                const weeks = Math.floor((expiry - start) / (7 * 24 * 3600 * 1000));
                return Math.max(1, weeks + 1);
            } else if (recurrence === "monthly") {
                const months = (expiry.getUTCFullYear() - start.getUTCFullYear()) * 12 + (expiry.getUTCMonth() - start.getUTCMonth());
                return Math.max(1, months + 1);
            }
        } catch { return 52; }
        return 52;
    }, [recurrence, startDate, membershipExpiresAt]);

    // Clamp the current occurrence count if the max shrinks (e.g., user picks a
    // later start date that eats into their membership window).
    React.useEffect(() => {
        if (recurrence && occurrenceCount > maxOccurrences) {
            setOccurrenceCount(maxOccurrences);
        }
    }, [maxOccurrences, recurrence]); // eslint-disable-line react-hooks/exhaustive-deps

    // Preview the last occurrence date for user reassurance.
    const lastDatePreview = React.useMemo(() => {
        if (!recurrence || !startDate || !occurrenceCount) return "";
        try {
            const start = new Date(startDate + "T00:00:00Z");
            if (recurrence === "weekly") {
                const d = new Date(start);
                d.setUTCDate(d.getUTCDate() + (occurrenceCount - 1) * 7);
                return d.toISOString().slice(0, 10);
            } else if (recurrence === "monthly") {
                const d = new Date(start);
                d.setUTCMonth(d.getUTCMonth() + (occurrenceCount - 1));
                return d.toISOString().slice(0, 10);
            }
        } catch { return ""; }
        return "";
    }, [recurrence, startDate, occurrenceCount]);

    const profileLabel = activeProfile === "trainer" ? "Trainer" : "Team Director / Coach";

    return (
        <div className="border border-[#2a2a2a] bg-[#0f0f0f] p-4 space-y-3" data-testid="event-recurrence-block">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff4500]">Recurring event · {profileLabel}</div>
                    <div className="text-[11px] text-[#a0a0a0]">Set a weekly practice or monthly session in one shot. Occurrences stop on or before your membership expiration.</div>
                </div>
                {membershipExpiresAt && (
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]" data-testid="membership-expiry-label">
                        Membership expires <span className="text-white">{membershipExpiresAt}</span>
                    </div>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Repeats</Label>
                    <Select value={recurrence || "none"} onValueChange={(v) => setRecurrence(v === "none" ? "" : v)}>
                        <SelectTrigger data-testid="event-recurrence" className="mt-2 h-11 rounded-none bg-[#141414] border-[#2a2a2a]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none">
                            <SelectItem value="none">Does not repeat</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {recurrence && (
                    <>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">
                                Number of {recurrence === "weekly" ? "weeks" : "months"}
                            </Label>
                            <Input
                                data-testid="event-occurrence-count"
                                type="number"
                                min={1}
                                max={maxOccurrences}
                                value={occurrenceCount}
                                onChange={(e) => {
                                    const n = parseInt(e.target.value || "1", 10);
                                    setOccurrenceCount(Math.max(1, Math.min(maxOccurrences, isNaN(n) ? 1 : n)));
                                }}
                                className="mt-2 h-11 rounded-none bg-[#141414] border-[#2a2a2a]"
                            />
                            <div className="text-[10px] text-[#a0a0a0] mt-1" data-testid="occurrence-count-hint">
                                Max within membership: <span className="text-white">{maxOccurrences}</span>
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Ends on</Label>
                            <div className="mt-2 h-11 flex items-center px-3 bg-[#141414] border border-[#2a2a2a] text-sm text-white" data-testid="occurrence-last-date">
                                {lastDatePreview || "—"}
                            </div>
                            <div className="text-[10px] text-[#a0a0a0] mt-1">Auto-calculated from repeats × count.</div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

